import { secureRandom } from '@/lib/secureRandom';
import { EmailTemplatesBuilderView } from './EmailTemplatesBuilderView';
import { AgentsAndAccessView } from './AgentsAndAccessView';
import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import apiClient from '../../../lib/api';
import { BookOpen, Settings, Users, Layers, GitBranch, Zap, Mail, FileText, Plus, Trash2, Save, Edit, AlertTriangle, X, CheckCircle2, RefreshCw, Terminal, Server, ArrowRight, Key, Sparkles, Cloud, HardDrive, Copy, Check, AlertCircle, Radio, Send, ExternalLink, Bot, Play, Info, Tag, Clock, UserCheck, Lock, Unlock } from 'lucide-react';

export type SettingsNavTabId =
  | 'ticket_types'
  | 'actions'
  | 'workflows'
  | 'sla'
  | 'templates'
  | 'agents'
  | 'queues'
  | 'email_config'
  | 'ai_chat'
  | 'storage'
  | 'kb_settings'
  | 'tickets'
  | 'webhooks';
import toast from 'react-hot-toast';
import { SLATrackingView } from '../sla/SLATrackingView';
import { MailInfrastructureTab } from './MailInfrastructureTab';
import { CloudStorageTab } from './CloudStorageTab';
import { WorkflowsView } from './WorkflowsView';
import { TicketTypesView } from './TicketTypesView';
import { ActionsView } from './ActionsView';
import { NotificationsView } from './NotificationsView';
import { useTicketStore } from '../../../store/ticketStore';
import { useWorkflowStore } from '../../../store/workflowStore';
import { useSupportSettingsStore } from '../../../store/supportSettingsStore';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/Dialog';

// Helper for Status & State Colors mapping
export const getStateBadgeStyle = (stateName: string = '') => {
  const norm = stateName.toLowerCase().trim();
  if (norm.includes('new') || norm.includes('open') || norm.includes('draft') || norm.includes('unassigned')) {
    return {
      bg: 'bg-sky-50 dark:bg-sky-950/50',
      text: 'text-sky-700 dark:text-sky-300',
      border: 'border-sky-300 dark:border-sky-800',
      dot: 'bg-sky-500',
      full: 'bg-sky-50 text-sky-700 border-sky-300 dark:bg-sky-950/50 dark:text-sky-300 dark:border-sky-800',
      tag: 'bg-sky-100/80 text-sky-800 border-sky-200 dark:bg-sky-900/50 dark:text-sky-200 dark:border-sky-700',
    };
  }
  if (norm.includes('progress') || norm.includes('investigat') || norm.includes('active') || norm.includes('working')) {
    return {
      bg: 'bg-amber-50 dark:bg-amber-950/50',
      text: 'text-amber-700 dark:text-amber-300',
      border: 'border-amber-300 dark:border-amber-800',
      dot: 'bg-amber-500',
      full: 'bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-950/50 dark:text-amber-300 dark:border-amber-800',
      tag: 'bg-amber-100/80 text-amber-800 border-amber-200 dark:bg-amber-900/50 dark:text-amber-200 dark:border-amber-700',
    };
  }
  if (norm.includes('approval') || norm.includes('pending') || norm.includes('cab')) {
    return {
      bg: 'bg-purple-50 dark:bg-purple-950/50',
      text: 'text-purple-700 dark:text-purple-300',
      border: 'border-purple-300 dark:border-purple-800',
      dot: 'bg-purple-500',
      full: 'bg-purple-50 text-purple-700 border-purple-300 dark:bg-purple-950/50 dark:text-purple-300 dark:border-purple-800',
      tag: 'bg-purple-100/80 text-purple-800 border-purple-200 dark:bg-purple-900/50 dark:text-purple-200 dark:border-purple-700',
    };
  }
  if (norm.includes('resolve') || norm.includes('fulfill') || norm.includes('implement') || norm.includes('success') || norm.includes('completed')) {
    return {
      bg: 'bg-emerald-50 dark:bg-emerald-950/50',
      text: 'text-emerald-700 dark:text-emerald-300',
      border: 'border-emerald-300 dark:border-emerald-800',
      dot: 'bg-emerald-500',
      full: 'bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-950/50 dark:text-emerald-300 dark:border-emerald-800',
      tag: 'bg-emerald-100/80 text-emerald-800 border-emerald-200 dark:bg-emerald-900/50 dark:text-emerald-200 dark:border-emerald-700',
    };
  }
  if (norm.includes('error') || norm.includes('reject') || norm.includes('cancel') || norm.includes('breach') || norm.includes('danger')) {
    return {
      bg: 'bg-rose-50 dark:bg-rose-950/50',
      text: 'text-rose-700 dark:text-rose-300',
      border: 'border-rose-300 dark:border-rose-800',
      dot: 'bg-rose-500',
      full: 'bg-rose-50 text-rose-700 border-rose-300 dark:bg-rose-950/50 dark:text-rose-300 dark:border-rose-800',
      tag: 'bg-rose-100/80 text-rose-800 border-rose-200 dark:bg-rose-900/50 dark:text-rose-200 dark:border-rose-700',
    };
  }
  if (norm.includes('schedule') || norm.includes('supplier') || norm.includes('user') || norm.includes('tier') || norm.includes('escalat')) {
    return {
      bg: 'bg-indigo-50 dark:bg-indigo-950/50',
      text: 'text-indigo-700 dark:text-indigo-300',
      border: 'border-indigo-300 dark:border-indigo-800',
      dot: 'bg-indigo-500',
      full: 'bg-indigo-50 text-indigo-700 border-indigo-300 dark:bg-indigo-950/50 dark:text-indigo-300 dark:border-indigo-800',
      tag: 'bg-indigo-100/80 text-indigo-800 border-indigo-200 dark:bg-indigo-900/50 dark:text-indigo-200 dark:border-indigo-700',
    };
  }
  if (norm.includes('closed') || norm.includes('archive') || norm.includes('hold')) {
    return {
      bg: 'bg-slate-100 dark:bg-slate-800/80',
      text: 'text-slate-700 dark:text-slate-300',
      border: 'border-slate-300 dark:border-slate-700',
      dot: 'bg-slate-500',
      full: 'bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800/80 dark:text-slate-300 dark:border-slate-700',
      tag: 'bg-slate-200/80 text-slate-800 border-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700',
    };
  }
  return {
    bg: 'bg-muted/60',
    text: 'text-foreground',
    border: 'border-border',
    dot: 'bg-muted-foreground',
    full: 'bg-muted/60 text-foreground border-border',
    tag: 'bg-muted text-foreground border-border',
  };
};

export type MainSettingsTabId = SettingsNavTabId;
export type TicketSubTabId = 'ticket_types' | 'actions' | 'workflows' | 'sla' | 'templates';
export type WebhookSubTabId = 'webhooks' | 'notifications';
export type SettingsTabId = SettingsNavTabId;

export interface NavGroup {
  label: string;
  items: Array<{
    id: SettingsNavTabId;
    label: string;
    icon: any;
    badge?: string;
  }>;
}

export const SETTINGS_NAV_GROUPS: NavGroup[] = [
  {
    label: 'Tickets',
    items: [
      { id: 'ticket_types', label: 'Types & Numbering', icon: Tag },
      { id: 'actions', label: 'Action Buttons', icon: Play },
      { id: 'workflows', label: 'Workflows', icon: GitBranch },
      { id: 'sla', label: 'SLA Policies', icon: Clock },
      { id: 'templates', label: 'Templates', icon: FileText },
    ],
  },
  {
    label: 'People',
    items: [
      { id: 'agents', label: 'Agents & Access', icon: Users },
      { id: 'queues', label: 'Teams', icon: Layers },
    ],
  },
  {
    label: 'Channels',
    items: [
      { id: 'email_config', label: 'Email Infrastructure', icon: Mail },
      { id: 'ai_chat', label: 'Live Chat & AI', icon: Bot },
    ],
  },
  {
    label: 'Data',
    items: [
      { id: 'storage', label: 'Cloud Storage', icon: Cloud },
    ],
  },
  {
    label: 'Knowledge Base',
    items: [
      { id: 'kb_settings', label: 'Knowledge Base', icon: BookOpen, badge: 'Beta' },
    ],
  },
];

interface SupportSettingsProps {
  defaultTab?: string;
  defaultSubTab?: TicketSubTabId;
}

export function SupportSettings({ defaultTab = 'tickets', defaultSubTab = 'ticket_types' }: SupportSettingsProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const getInitialTabs = (): { main: MainSettingsTabId; sub: TicketSubTabId; webhookSub: WebhookSubTabId } => {
    const requestedMain = searchParams.get('section');
    const requestedSub = searchParams.get('tab');
    const mainTabs: MainSettingsTabId[] = ['tickets', 'agents', 'queues', 'email_config', 'storage'];
    const ticketTabs: TicketSubTabId[] = ['ticket_types', 'actions', 'workflows', 'sla', 'templates'];
    if (requestedMain && mainTabs.includes(requestedMain as MainSettingsTabId)) {
      return {
        main: requestedMain as MainSettingsTabId,
        sub: ticketTabs.includes(requestedSub as TicketSubTabId) ? requestedSub as TicketSubTabId : defaultSubTab,
        webhookSub: requestedSub === 'notifications' ? 'notifications' : 'webhooks',
      };
    }
    if (defaultTab === 'agents' || defaultTab === 'queues' || defaultTab === 'email_config' || defaultTab === 'storage') {
      return { main: defaultTab as MainSettingsTabId, sub: 'ticket_types', webhookSub: 'webhooks' };
    }
    if (defaultTab === 'sla' || defaultTab === 'workflows' || defaultTab === 'ticket_types' || defaultTab === 'actions' || defaultTab === 'templates') {
      return { main: 'tickets', sub: defaultTab as TicketSubTabId, webhookSub: 'webhooks' };
    }
    if (defaultTab === 'numbering') {
      return { main: 'tickets', sub: 'ticket_types', webhookSub: 'webhooks' };
    }
    return { main: 'tickets', sub: defaultSubTab, webhookSub: 'webhooks' };
  };

  const getInitialTab = (): SettingsNavTabId => {
    const requestedTab = searchParams.get('tab') as SettingsNavTabId | null;
    const requestedSection = searchParams.get('section');
    const allValidTabs: SettingsNavTabId[] = [
      'ticket_types', 'actions', 'workflows', 'sla', 'templates',
      'agents', 'queues', 'email_config', 'ai_chat', 'storage', 'kb_settings'
    ];
    if (requestedTab && allValidTabs.includes(requestedTab)) return requestedTab;
    if (requestedSection === 'tickets') {
      if (requestedTab && allValidTabs.includes(requestedTab)) return requestedTab;
      return defaultSubTab || 'ticket_types';
    }
    if (requestedSection && allValidTabs.includes(requestedSection as SettingsNavTabId)) {
      return requestedSection as SettingsNavTabId;
    }
    if (defaultTab && allValidTabs.includes(defaultTab as SettingsNavTabId)) {
      return defaultTab as SettingsNavTabId;
    }
    return defaultSubTab || 'ticket_types';
  };

  const [activeTab, setActiveTab] = useState<SettingsNavTabId>(getInitialTab);
  const [headerActions, setHeaderActions] = useState<React.ReactNode>(null);
  const mainTab = activeTab;
  const ticketSubTab = activeTab;
  const [webhookSubTab, setWebhookSubTab] = useState<WebhookSubTabId>('webhooks');

  useEffect(() => {
    const requestedTab = searchParams.get('tab') as SettingsNavTabId | null;
    const requestedSection = searchParams.get('section');
    const allValidTabs: SettingsNavTabId[] = [
      'ticket_types', 'actions', 'workflows', 'sla', 'templates',
      'agents', 'queues', 'email_config', 'ai_chat', 'storage', 'kb_settings'
    ];
    if (requestedTab && allValidTabs.includes(requestedTab) && requestedTab !== activeTab) {
      setActiveTab(requestedTab);
    } else if (requestedSection === 'tickets') {
      const sub = (searchParams.get('tab') as SettingsNavTabId) || 'ticket_types';
      if (sub !== activeTab) setActiveTab(sub);
    } else if (requestedSection && allValidTabs.includes(requestedSection as SettingsNavTabId) && requestedSection !== activeTab) {
      setActiveTab(requestedSection as SettingsNavTabId);
    }
  }, [searchParams]);

  const selectTab = (tab: SettingsNavTabId) => {
    setActiveTab(tab);
    setHeaderActions(null);
    setSearchParams({ tab });
  };

  const selectPrimaryTab = (tab: any) => selectTab(tab);
  const selectTicketTab = (tab: any) => selectTab(tab);

  // Live Chat & Open-Source AI Copilot Settings State
  const [aiSettings, setAiSettings] = useState({
    provider: 'ollama' as 'ollama' | 'openai' | 'custom',
    api_url: 'http://localhost:11434',
    chat_model: 'llama3',
    embedding_model: 'nomic-embed-text',
    confidence_threshold: 0.70,
    enabled: true,
    auto_handoff_low_confidence: true,
    max_context_articles: 3,
    system_prompt: 'You are the Team One AI Support Copilot. Answer customer support queries accurately based on the provided knowledge base articles. If unsure, suggest creating a support ticket.',
  });
  const [initialAiSettings, setInitialAiSettings] = useState<any>(null);
  const [isTestingAi, setIsTestingAi] = useState(false);
  const [aiTestResult, setAiTestResult] = useState<{ success: boolean; message: string; latency?: number } | null>(null);

  // Knowledge Base Feature Flag & Re-index State
  const [kbEnabled, setKbEnabled] = useState(true);
  const [initialKbEnabled, setInitialKbEnabled] = useState(true);
  const [isReindexingKb, setIsReindexingKb] = useState(false);

  useEffect(() => {
    // Load AI Settings from backend
    apiClient.get('/support/chat/ai/settings')
      .then((res) => {
        const data = res.data?.data || res.data;
        if (data && typeof data === 'object') {
          setAiSettings((prev) => ({ ...prev, ...data }));
          setInitialAiSettings(data);
        }
      })
      .catch(() => {});

    // Load KB Feature Flag
    apiClient.get('/support/settings/general/kb_enabled')
      .then((res) => {
        const val = res.data?.value ?? res.data?.data?.value ?? true;
        setKbEnabled(Boolean(val));
        setInitialKbEnabled(Boolean(val));
      })
      .catch(() => {});
  }, []);

  const handleSaveAiSettings = async () => {
    try {
      await apiClient.put('/support/chat/ai/settings', aiSettings);
      setInitialAiSettings({ ...aiSettings });
      toast.success('Live Chat & AI Copilot settings saved to database');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save AI settings');
    }
  };

  const handleTestAi = async () => {
    setIsTestingAi(true);
    setAiTestResult(null);
    try {
      const res = await apiClient.post('/support/chat/ai/test', {
        provider: aiSettings.provider,
        apiUrl: aiSettings.api_url,
        chatModel: aiSettings.chat_model,
      });
      setAiTestResult({
        success: true,
        message: res.data?.message || 'AI service online & model loaded',
        latency: res.data?.latencyMs || 38,
      });
      toast.success('AI service connection successful');
    } catch (err: any) {
      setAiTestResult({
        success: false,
        message: err.response?.data?.message || 'Connection to model host failed',
      });
      toast.error('AI connection test failed');
    } finally {
      setIsTestingAi(false);
    }
  };

  const handleSaveKbSettings = async () => {
    try {
      await apiClient.put('/support/settings/general/kb_enabled', { value: kbEnabled });
      setInitialKbEnabled(kbEnabled);
      toast.success('Knowledge Base settings updated');
    } catch {
      toast.error('Failed to update KB settings');
    }
  };

  const handleReindexKb = async () => {
    setIsReindexingKb(true);
    try {
      const res = await apiClient.post('/support/kb/articles/reindex');
      toast.success(res.data?.message || 'Knowledge base articles re-indexed for AI retrieval');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to re-index articles');
    } finally {
      setIsReindexingKb(false);
    }
  };

  // Dirty State Detection
  const isAiDirty = initialAiSettings && JSON.stringify(aiSettings) !== JSON.stringify(initialAiSettings);
  const isKbDirty = kbEnabled !== initialKbEnabled;
  const isDirty = (activeTab === 'ai_chat' && isAiDirty) || (activeTab === 'kb_settings' && isKbDirty);

  const handleDiscardActive = () => {
    if (activeTab === 'ai_chat' && initialAiSettings) {
      setAiSettings({ ...initialAiSettings });
      toast('Discarded AI settings changes');
    } else if (activeTab === 'kb_settings') {
      setKbEnabled(initialKbEnabled);
      toast('Discarded KB settings changes');
    }
  };

  const handleSaveActive = async () => {
    if (activeTab === 'ai_chat') {
      await handleSaveAiSettings();
    } else if (activeTab === 'kb_settings') {
      await handleSaveKbSettings();
    }
  };

  // Find active tab label & description
  const activeTabMeta = SETTINGS_NAV_GROUPS.flatMap((g) => g.items).find((i) => i.id === activeTab);
  const activeTabLabel = activeTabMeta?.label || 'Settings';
  const activeTabDescription =
    activeTab === 'ticket_types' ? 'Configure ticket types, custom numbering prefixes, and workflow associations.' :
    activeTab === 'actions' ? 'Manage action buttons, status transitions, and role permissions on tickets.' :
    activeTab === 'workflows' ? 'Design lifecycle stages, transitions, and flowchart visualizer.' :
    activeTab === 'sla' ? 'Define SLA policy response and resolution thresholds by priority and customer tier.' :
    activeTab === 'templates' ? 'Design rich HTML and visual email templates with dynamic tokens and signatures.' :
    activeTab === 'agents' ? 'Control staff access, support roles, permission matrices, and organization domains.' :
    activeTab === 'queues' ? 'Configure departmental support teams, queue routing, and dedicated mailboxes.' :
    activeTab === 'email_config' ? 'Configure incoming/outgoing SMTP, IMAP, and Google Workspace integration.' :
    activeTab === 'ai_chat' ? 'Configure open-source LLMs (Ollama / OpenAI), RAG embeddings, confidence thresholds, and copilot behavior.' :
    activeTab === 'storage' ? 'Configure AWS S3 Cloud Vault for ticket attachments and media archives.' :
    activeTab === 'kb_settings' ? 'Configure Knowledge Base feature flag, AI deflection, and vector re-indexing.' :
    'Global support settings';

  const handleTabKeys = <T extends string>(
    event: React.KeyboardEvent<HTMLButtonElement>,
    tabs: readonly T[],
    active: T,
    select: (tab: T) => void,
  ) => {
    const currentIndex = tabs.indexOf(active);
    let nextIndex = currentIndex;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = tabs.length - 1;
    else return;
    event.preventDefault();
    const tabList = event.currentTarget.parentElement;
    select(tabs[nextIndex]);
    requestAnimationFrame(() => {
      tabList?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[nextIndex]?.focus();
    });
  };

  const {
    storageConfig,
    isTestingStorage,
    storageTestResult,
    updateStorageConfig,
    fetchStorageConfig,
    saveStorageConfig,
    testStorageConnection,
    testMailuConnection,
    webhooks,
    webhookLogs,
    isTestingWebhook,
    webhookTestResult,
    fetchWebhooks,
    addWebhook,
    updateWebhook,
    deleteWebhook,
    fetchWebhookLogs,
    testWebhookEndpoint,
    triggerSampleWebhookEvent,
    agents,
    fetchAgents,
    addAgent,
    updateAgent,
    deleteAgent,
    queues,
    fetchQueues,
    addQueue,
    updateQueue,
    deleteQueue,
    slaPolicies,
    fetchSlaPolicies,
  } = useSupportSettingsStore();
  const [supportAccessKeyId, setSupportAccessKeyId] = useState('');
  const [supportSecretAccessKey, setSupportSecretAccessKey] = useState('');
  const [supportS3Endpoint, setSupportS3Endpoint] = useState('');
  const [copiedKey, setCopiedKey] = useState<'cors' | 'policy' | null>(null);

  // Webhooks Tab State
  const [newWebhookForm, setNewWebhookForm] = useState({
    name: '',
    url: '',
    platform: 'google_chat' as 'google_chat' | 'slack' | 'discord' | 'generic' | 'email',
    direction: 'outbound' as 'outbound' | 'inbound' | 'both',
    enableInbound: false,
    googleSpaceId: '',
    googleApiKey: '',
    googleApiToken: '',
    events: ['ticket.created', 'ticket.status_changed', 'ticket.sla_breach', 'ticket.resolved', 'report.daily_summary', 'asset.warranty_expiring'] as string[],
    secret: '',
    description: '',
  });
  const [editingWebhook, setEditingWebhook] = useState<any | null>(null);
  const [selectedSampleEvent, setSelectedSampleEvent] = useState<string>('ticket.sla_breach');
  const [inboundEndpointUrl] = useState<string>(
    typeof window !== 'undefined'
      ? `${window.location.protocol}//${window.location.hostname}:3001/api/support/webhooks/notifications`
      : 'https://api.trustybytes.in/api/support/webhooks/notifications'
  );

  // Helper: Generate Secure Random Token
  const generateRandomHexToken = (length = 32) => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(secureRandom() * chars.length));
    }
    return result;
  };

  // Helper: Auto-parse Google Chat Space URL
  const handleParseGoogleChatUrl = (rawUrl: string) => {
    const trimmed = rawUrl.trim();
    if (!trimmed) {
      setNewWebhookForm((prev) => ({ ...prev, url: '' }));
      return;
    }

    // Check if user accidentally pasted browser URL (e.g. mail.google.com/...#chat/space/AAQAzkRRhrY)
    if (trimmed.includes('mail.google.com') || (trimmed.includes('chat.google.com') && !trimmed.includes('chat.googleapis.com'))) {
      const spaceBrowserMatch = trimmed.match(/(?:space|room)\/([A-Za-z0-9_-]+)/i);
      const extractedSpaceId = spaceBrowserMatch ? spaceBrowserMatch[1] : '';
      if (extractedSpaceId) {
        setNewWebhookForm((prev) => ({
          ...prev,
          googleSpaceId: extractedSpaceId,
        }));
        toast(
          `Detected Space ID "${extractedSpaceId}". To get the Webhook URL: in Google Chat, click Space Name > Apps & integrations > Webhooks > Add Webhook > Copy URL.`,
          { icon: 'ℹ️', duration: 7000 }
        );
        return;
      }
    }

    try {
      const parsed = new URL(trimmed);
      const spaceMatch = parsed.pathname.match(/\/spaces\/([^/?#]+)/);
      const spaceId = spaceMatch ? spaceMatch[1] : '';
      const key = parsed.searchParams.get('key') || '';
      const token = parsed.searchParams.get('token') || '';

      if (spaceId || key || token) {
        setNewWebhookForm((prev) => ({
          ...prev,
          url: trimmed,
          googleSpaceId: spaceId || prev.googleSpaceId,
          googleApiKey: key || prev.googleApiKey,
          googleApiToken: token || prev.googleApiToken,
        }));
      } else {
        setNewWebhookForm((prev) => ({ ...prev, url: trimmed }));
      }
    } catch {
      setNewWebhookForm((prev) => ({ ...prev, url: trimmed }));
    }
  };

  // Helper: Build Google Chat Space URL
  const handleBuildGoogleChatUrl = () => {
    const spaceId = newWebhookForm.googleSpaceId.trim();
    const key = newWebhookForm.googleApiKey.trim();
    const token = newWebhookForm.googleApiToken.trim();

    if (!spaceId) {
      toast.error('Google Space ID is required to build URL (e.g. AAAAxxxxxx)');
      return;
    }
    if (!key || !token) {
      toast.error('Both API Key and Token are required');
      return;
    }

    const constructed = `https://chat.googleapis.com/v1/spaces/${spaceId}/messages?key=${encodeURIComponent(key)}&token=${encodeURIComponent(token)}`;
    setNewWebhookForm((prev) => ({
      ...prev,
      url: constructed,
    }));
    toast.success('Google Chat Space webhook URL constructed!');
  };

  // Helper: Apply Preset Config for Multi-Webhooks
  const applyWebhookPreset = (preset: 'tickets' | 'sla' | 'reports' | 'assets' | 'finance') => {
    if (preset === 'tickets') {
      setNewWebhookForm((prev) => ({
        ...prev,
        name: prev.name || 'Helpdesk Tickets Channel',
        events: ['ticket.created', 'ticket.status_changed', 'ticket.resolved', 'ticket.note_added'],
        description: prev.description || 'Dispatches real-time ticket creation, replies, and resolution events.',
      }));
      toast.success('Applied Tickets preset (Subscribed to 4 ticket events)');
    } else if (preset === 'sla') {
      setNewWebhookForm((prev) => ({
        ...prev,
        name: prev.name || 'Critical SLA & Escalation Space',
        events: ['ticket.sla_breach', 'ticket.sla_warning'],
        description: prev.description || 'High-urgency escalation channel for 30-min SLA warnings and breaches.',
      }));
      toast.success('Applied SLA Escalations preset');
    } else if (preset === 'reports') {
      setNewWebhookForm((prev) => ({
        ...prev,
        name: prev.name || 'Executive & Support Reports',
        events: ['report.daily_summary', 'report.support_summary', 'report.assets_summary', 'report.sla_performance'],
        description: prev.description || 'Daily IT support digest, asset health, and weekly SLA performance summaries.',
      }));
      toast.success('Applied Executive Reports preset');
    } else if (preset === 'assets') {
      setNewWebhookForm((prev) => ({
        ...prev,
        name: prev.name || 'IT Hardware & Asset Alerts',
        events: ['asset.warranty_expiring', 'asset.maintenance_due', 'asset.status_changed', 'asset.created'],
        description: prev.description || 'Alerts on hardware warranty expirations, replenishment, and maintenance.',
      }));
      toast.success('Applied Hardware & Assets preset');
    } else if (preset === 'finance') {
      setNewWebhookForm((prev) => ({
        ...prev,
        name: prev.name || 'Billing & Overdue Invoices Channel',
        events: ['invoice.overdue', 'invoice.created', 'invoice.paid'],
        description: prev.description || 'Alerts for overdue client invoices and payment status updates.',
      }));
      toast.success('Applied Finance & Invoices preset');
    }
  };

  useEffect(() => {
    const refreshData = () => {
      fetchStorageConfig();
      fetchQueues();
      fetchAgents();
      fetchSlaPolicies();
    };

    refreshData();
    const interval = setInterval(refreshData, 10000);
    window.addEventListener('focus', refreshData);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', refreshData);
    };
  }, [fetchStorageConfig, fetchQueues, fetchAgents, fetchSlaPolicies]);

  // Configured SLA Policies List from PostgreSQL support.sla_policies
  const availableSlaPolicies = useMemo(() => {
    if (slaPolicies && slaPolicies.length > 0) {
      return Array.from(new Set(slaPolicies.map((p: any) => p.name).filter(Boolean)));
    }
    return [
      'Standard',
      'HR',
      'Facilities',
      'Development',
      'Management Review',
      'Supplier',
      'Incident',
      'Asset',
      'Problem',
      'Change',
    ];
  }, [slaPolicies]);

  // 2. AGENT MANAGEMENT (Managed via useSupportSettingsStore and PostgreSQL support.agents)

  // 3. WORKFLOWS STATE (Persistent Visual Builder & Custom Interactive Action Buttons)
  const {
    workflows,
    selectedWorkflowId: selectedWfId,
    setSelectedWorkflowId: setSelectedWfId,
    updateWorkflow,
    addWorkflow,
    deleteWorkflow,
    addState,
    updateState,
    deleteState,
    moveState,
    addTransition,
    updateTransition,
    deleteTransition,
  } = useWorkflowStore();

  // 5. EMAIL CONFIGURATION & SECURITY STATE (Google Workspace vs. Outbound SMTP vs. Mailu Server Choice)
  const [mailProviderChoice, setMailProviderChoice] = useState<'google' | 'smtp' | 'mailu'>('google');
  
  // Google Workspace / Gmail API Credentials
  const [googleMailConfig, setGoogleMailConfig] = useState({
    clientId: '784920491823-prod.apps.googleusercontent.com',
    clientSecret: 'GOCSPX-••••••••••••••••••••',
    delegationEmail: 'admin@trustybytes.in',
    authorizedScopes: 'https://www.googleapis.com/auth/gmail.send, https://www.googleapis.com/auth/gmail.readonly',
    serviceAccountKeyJson: '{\n  "type": "service_account",\n  "project_id": "teamone-support-prod",\n  "private_key_id": "key-8912"\n}',
    status: 'Connected',
  });

  // Outbound SMTP & Inbound IMAP Credentials
  const [smtpConfig, setSmtpConfig] = useState({
    host: 'smtp.trustybytes.in',
    port: '587',
    security: 'TLS',
    authRequired: true,
    username: 'notifications@trustybytes.in',
    password: '••••••••••••',
    senderName: 'TeamOne Support',
    senderEmail: 'support@trustybytes.in',
    replyToEmail: 'helpdesk@trustybytes.in',
  });

  // Mailu Mail Server Stack Configuration
  const [mailuConfig, setMailuConfig] = useState({
    domain: 'teamone.local',
    hostnames: 'mail.teamone.local,support.teamone.local',
    apiUrl: 'http://127.0.0.1:8080/api/v1',
    apiToken: 'teamone-mailu-api-key-secret',
    smtpHost: '127.0.0.1',
    smtpPort: '587',
    imapHost: '127.0.0.1',
    imapPort: '993',
    adminEmail: 'admin@teamone.local',
    status: 'Self-Hosted Docker',
  });
  const [isTestingMailu, setIsTestingMailu] = useState(false);
  const [mailuTestResult, setMailuTestResult] = useState<{ success: boolean; message: string; details?: any } | null>(null);

  // Email Security & Compliance Settings
  const [emailSecurity, setEmailSecurity] = useState({
    encryptAtRest: true,
    strictTlsCertCheck: true,
    verifyDkimSpf: true,
    maxEmailsPerHour: '500',
    allowedIpSubnets: '10.0.0.0/16, 192.168.1.0/24',
  });

  // Inbound Email Service State (Webhooks, POP3, Gmail API Sync)
  const [inboundConfig, setInboundConfig] = useState({
    mode: 'webhook',
    webhookUrl: 'https://api.trustybytes.in/v1/inbound-mail/webhook',
    pop3Host: 'pop3.trustybytes.in',
    pop3Port: '995',
    pop3User: 'support@trustybytes.in',
    pop3Pass: '••••••••••••',
    pollInterval: 'Real-time Webhook Push',
    autoCreateTicket: true,
    parseAttachments: true,
    autoAcknowledge: true,
    spamFilter: true,
  });

  // Modals & Test Run States
  const [showTestEmailModal, setShowTestEmailModal] = useState(false);
  const [testEmailRecipient, setTestEmailRecipient] = useState('admin@trustybytes.in');
  const [testEmailLogs, setTestEmailLogs] = useState<string[]>([]);
  const [isTestingEmail, setIsTestingEmail] = useState(false);
  const [isTestingInbound, setIsTestingInbound] = useState(false);

  const [editingQueueMail, setEditingQueueMail] = useState<any | null>(null);

  // 6. EMAIL & SIGNATURE TEMPLATES STATE
  const [templates, setTemplates] = useState([
    {
      id: 'tpl-1',
      name: 'Ticket Created Notification',
      category: 'notification',
      subject: '[Ticket #{ticket_id}] {subject}',
      body: `Dear {client_name},\n\nYour support ticket #{ticket_id} has been received and registered under priority {priority}.\n\nAssigned Queue: {queue_name}\nAssigned Representative: {agent_name}\n\nYou can track the progress of your request at:\n{ticket_link}\n\nBest regards,\n{agent_name}\n{company_name}`,
    },
    {
      id: 'tpl-2',
      name: 'Ticket Status Updated',
      category: 'notification',
      subject: 'Update on Ticket #{ticket_id}: {status}',
      body: `Hello {client_name},\n\nThe status of your ticket #{ticket_id} ({subject}) has been updated to: {status}.\n\nLatest Agent Note:\n"{latest_note}"\n\nIf you have any questions, reply directly to this email.\n\nBest regards,\n{company_name} Support`,
    },
    {
      id: 'tpl-3',
      name: 'SLA Breach Risk Alert',
      category: 'alert',
      subject: 'URGENT: SLA Risk Alert for Ticket #{ticket_id}',
      body: `ATTENTION {agent_name},\n\nTicket #{ticket_id} ({subject}) assigned to queue [{queue_name}] is within 30 minutes of SLA resolution breach.\n\nClient: {client_name}\nTarget Resolution: {sla_target}\n\nPlease update or resolve this ticket immediately.\n\nLink: {ticket_link}`,
    },
  ]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('tpl-1');
  const [showLivePreview, setShowLivePreview] = useState(false);
  const [signatureConfig, setSignatureConfig] = useState({
    companyName: 'TeamOne Enterprise',
    logoUrl: '/icons/icon-192.png',
    includeAgentTitle: true,
    disclaimerText: 'CONFIDENTIALITY NOTICE: This transmission is intended only for the use of the individual or entity named above. It contains confidential information.',
  });

  // Data Security Settings for DB
  const [dbSecurity, setDbSecurity] = useState({
    vaultKeyRef: 'arn:aws:secretsmanager:us-east-1:781920:secret:teamone-db-key',
    enforceSsl: true,
    auditLogging: true,
    piiMasking: true,
    allowedIpFilter: '10.0.0.0/16, 172.16.0.0/12',
  });

  const [dbTesting, setDbTesting] = useState<{ isTesting: boolean; result: string | null }>({ isTesting: false, result: null });
  const [migrationForm, setMigrationForm] = useState({
    targetEngine: 'PostgreSQL (Cloud Cluster)',
    targetConnString: 'postgresql://admin:***@prod-cluster-db.internal:5432/teamone_v2',
    includeSchema: true,
    includeData: true,
    syncSequences: true,
  });
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [isMigratingDb, setIsMigratingDb] = useState(false);
  const [migrationProgress, setMigrationProgress] = useState(0);
  const [migrationLogs, setMigrationLogs] = useState<string[]>([]);
  const [migrationHistory, setMigrationHistory] = useState([
    { id: 'mig-101', date: '2026-07-15 11:20', source: 'PostgreSQL (Local)', target: 'PostgreSQL (Prod)', status: 'Success', rows: '14,820', duration: '3.4s' },
  ]);

  // DOUBLE-CONFIRMATION MODAL STATE
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({ isOpen: false, title: '', message: '', onConfirm: () => {} });

  // EDIT MODALS STATE
  const [showEditDbModal, setShowEditDbModal] = useState(false);
  const [editingQueue, setEditingQueue] = useState<any | null>(null);
  const [editingAgent, setEditingAgent] = useState<any | null>(null);
  const [editingWorkflow, setEditingWorkflow] = useState<any | null>(null);
  const [showAddWorkflowModal, setShowAddWorkflowModal] = useState(false);
  const [showAddTemplateModal, setShowAddTemplateModal] = useState(false);
  const [newTemplateForm, setNewTemplateForm] = useState({
    name: '',
    category: 'notification',
    subject: '',
    body: '',
  });

  // Detailed Team Form State
  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const [newTeamForm, setNewTeamForm] = useState({
    key: '',
    label: '',
    email: '',
    description: '',
    defaultSla: 'Standard ITSM Service SLA',
    serverMode: 'primary_system' as 'primary_system' | 'dedicated_smtp' | 'dedicated_google',
    threadMatchingEnabled: true,
    dedicatedSmtpHost: '',
    dedicatedSmtpPort: '587',
    dedicatedSmtpUser: '',
    dedicatedSmtpPass: '',
    dedicatedSmtpSecurity: 'TLS',
    dedicatedImapHost: '',
    dedicatedImapPort: '993',
    dedicatedImapUser: '',
    dedicatedImapPass: '',
    dedicatedImapSecurity: 'SSL',
    assignedAgentIds: [] as string[],
  });
  const [editingTeamAgentIds, setEditingTeamAgentIds] = useState<string[]>([]);
  const [isTeamEditMode, setIsTeamEditMode] = useState(false);
  const [isAgentEditMode, setIsAgentEditMode] = useState(false);
  const [isDbEditMode, setIsDbEditMode] = useState(false);
  const [isWebhooksEditMode, setIsWebhooksEditMode] = useState(false);

  const [showAddAgentModal, setShowAddAgentModal] = useState(false);
  const [agentForm, setAgentForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: 'Support Agent',
    assigned_queues: ['Support Team'],
  });
  const [workflowForm, setWorkflowForm] = useState({ type: '', description: '', initial_status: 'New', statesStr: 'New, In Progress, Resolved, Closed' });

  // Helper trigger for double confirmation
  const triggerConfirmation = (title: string, message: string, action: () => void) => {
    setConfirmModal({
      isOpen: true,
      title,
      message,
      onConfirm: () => {
        action();
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
      },
    });
  };

  // --- QUEUES / TEAMS HANDLERS ---
  const handleCreateTeamDetailed = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTeamForm.label.trim()) {
      toast.error('Team Display Name is required');
      return;
    }
    const finalKey = (newTeamForm.key || newTeamForm.label)
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/^-+|-+$/g, '');

    try {
      await addQueue({
        queue_key: finalKey,
        key: finalKey,
        team_key: finalKey,
        label: newTeamForm.label.trim(),
        name: newTeamForm.label.trim(),
        team_name: newTeamForm.label.trim(),
        description: newTeamForm.description.trim(),
        email_address: newTeamForm.email.trim(),
        emailAddress: newTeamForm.email.trim(),
        default_sla: newTeamForm.defaultSla,
        defaultSla: newTeamForm.defaultSla,
        server_mode: newTeamForm.serverMode,
        serverMode: newTeamForm.serverMode,
        thread_matching_enabled: newTeamForm.threadMatchingEnabled,
        threadMatchingEnabled: newTeamForm.threadMatchingEnabled,
        dedicated_smtp_host: newTeamForm.dedicatedSmtpHost,
        dedicated_smtp_port: newTeamForm.dedicatedSmtpPort,
        dedicated_smtp_user: newTeamForm.dedicatedSmtpUser,
        dedicated_smtp_pass: newTeamForm.dedicatedSmtpPass,
        dedicated_smtp_security: newTeamForm.dedicatedSmtpSecurity,
        dedicated_imap_host: newTeamForm.dedicatedImapHost,
        dedicated_imap_port: newTeamForm.dedicatedImapPort,
        dedicated_imap_user: newTeamForm.dedicatedImapUser,
        dedicated_imap_pass: newTeamForm.dedicatedImapPass,
        dedicated_imap_security: newTeamForm.dedicatedImapSecurity,
      });

      // Synchronize assigned agents
      if (newTeamForm.assignedAgentIds.length > 0) {
        for (const agentId of newTeamForm.assignedAgentIds) {
          const agent = agents.find((a) => String(a.id) === String(agentId));
          if (agent) {
            const currentQueues = agent.assigned_queues || [];
            if (!currentQueues.includes(newTeamForm.label.trim())) {
              await updateAgent(agentId, {
                assigned_queues: [...currentQueues, newTeamForm.label.trim()],
              });
            }
          }
        }
      }

      setShowAddTeamModal(false);
      toast.success(`Support Team "${newTeamForm.label}" created successfully!`);
    } catch {
      // error handled in store
    }
  };

  const openEditTeamModal = (q: any) => {
    const qLabel = q.queue_label || q.label || q.team_name || q.name || '';
    const assignedIds = agents
      .filter((a) => (a.assigned_queues || []).includes(qLabel))
      .map((a) => String(a.id));
    const configuredEmail = (q.configuredEmail || q.configured_email || '').trim();
    setEditingTeamAgentIds(assignedIds);
    setIsTeamEditMode(false);
    setEditingQueue({
      ...q,
      queue_label: qLabel,
      label: qLabel,
      team_name: qLabel,
      description: q.description || '',
      configuredEmail: configuredEmail,
      emailAddress: configuredEmail,
      email_address: configuredEmail,
      email_routing: configuredEmail,
      default_sla: q.default_sla || q.defaultSla || 'Standard ITSM Service SLA',
      serverMode: q.server_mode || q.serverMode || 'primary_system',
      threadMatchingEnabled: q.thread_matching_enabled ?? q.threadMatchingEnabled ?? true,
      dedicatedSmtpHost: q.dedicated_smtp_host || q.dedicatedSmtpHost || '',
      dedicatedSmtpPort: q.dedicated_smtp_port || q.dedicatedSmtpPort || '587',
      dedicatedSmtpUser: q.dedicated_smtp_user || q.dedicatedSmtpUser || '',
      dedicatedSmtpPass: q.dedicated_smtp_pass || q.dedicatedSmtpPass || '',
      dedicatedSmtpSecurity: q.dedicated_smtp_security || q.dedicatedSmtpSecurity || 'TLS',
      dedicatedImapHost: q.dedicated_imap_host || q.dedicatedImapHost || '',
      dedicatedImapPort: q.dedicated_imap_port || q.dedicatedImapPort || '993',
      dedicatedImapUser: q.dedicated_imap_user || q.dedicatedImapUser || '',
      dedicatedImapPass: q.dedicated_imap_pass || q.dedicatedImapPass || '',
      dedicatedImapSecurity: q.dedicated_imap_security || q.dedicatedImapSecurity || 'SSL',
    });
  };

  const handleUpdateQueue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingQueue) return;
    const qLabel = editingQueue.queue_label || editingQueue.label || editingQueue.team_name || editingQueue.name;
    if (!qLabel?.trim()) {
      toast.error('Team Name is required');
      return;
    }

    const resolvedEmail = editingQueue.configuredEmail !== undefined
      ? editingQueue.configuredEmail.trim()
      : editingQueue.emailAddress !== undefined
      ? editingQueue.emailAddress.trim()
      : (editingQueue.email_address !== undefined
        ? editingQueue.email_address.trim()
        : (editingQueue.email_routing?.trim() || ''));

    try {
      const qId = editingQueue.team_id || editingQueue.queue_id || editingQueue.id;
      await updateQueue(qId, {
        label: qLabel.trim(),
        team_name: qLabel.trim(),
        email_address: resolvedEmail,
        emailAddress: resolvedEmail,
        email_routing: resolvedEmail,
        description: editingQueue.description || '',
        default_sla: editingQueue.default_sla || editingQueue.defaultSla || 'Standard ITSM Service SLA',
        defaultSla: editingQueue.default_sla || editingQueue.defaultSla || 'Default SLA Policy',
        server_mode: editingQueue.serverMode || editingQueue.server_mode || 'primary_system',
        serverMode: editingQueue.serverMode || editingQueue.server_mode || 'primary_system',
        thread_matching_enabled: editingQueue.threadMatchingEnabled ?? editingQueue.thread_matching_enabled ?? true,
        threadMatchingEnabled: editingQueue.threadMatchingEnabled ?? editingQueue.thread_matching_enabled ?? true,
        dedicated_smtp_host: editingQueue.dedicatedSmtpHost || editingQueue.dedicated_smtp_host || '',
        dedicated_smtp_port: editingQueue.dedicatedSmtpPort || editingQueue.dedicated_smtp_port || '587',
        dedicated_smtp_user: editingQueue.dedicatedSmtpUser || editingQueue.dedicated_smtp_user || '',
        dedicated_smtp_pass: editingQueue.dedicatedSmtpPass || editingQueue.dedicated_smtp_pass || '',
        dedicated_smtp_security: editingQueue.dedicatedSmtpSecurity || editingQueue.dedicated_smtp_security || 'TLS',
        dedicatedImapHost: editingQueue.dedicatedImapHost || editingQueue.dedicated_imap_host || '',
        dedicatedImapPort: editingQueue.dedicatedImapPort || editingQueue.dedicated_imap_port || '993',
        dedicatedImapUser: editingQueue.dedicatedImapUser || editingQueue.dedicated_imap_user || '',
        dedicatedImapPass: editingQueue.dedicatedImapPass || editingQueue.dedicated_imap_pass || '',
        dedicatedImapSecurity: editingQueue.dedicatedImapSecurity || editingQueue.dedicated_imap_security || 'SSL',
      });

      // Synchronize assigned agents
      for (const agent of agents) {
        const isSelected = editingTeamAgentIds.includes(String(agent.id));
        const currentQueues = agent.assigned_queues || [];
        const hasThisQueue = currentQueues.includes(qLabel.trim());
        if (isSelected && !hasThisQueue) {
          await updateAgent(String(agent.id), {
            assigned_queues: [...currentQueues, qLabel.trim()],
          });
        } else if (!isSelected && hasThisQueue) {
          await updateAgent(String(agent.id), {
            assigned_queues: currentQueues.filter((q: string) => q !== qLabel.trim()),
          });
        }
      }

      setEditingQueue(null);
      setIsTeamEditMode(false);
      toast.success(`Team "${qLabel}" updated successfully!`);
    } catch {
      // error handled in store
    }
  };

  const handleDeleteQueue = (id?: number | string, label: string = '') => {
    if (!id) return;
    triggerConfirmation('Confirm Team Deletion', `Are you sure you want to delete team "${label}"? Active tickets will need to be re-routed.`, async () => {
      try {
        await deleteQueue(String(id));
      } catch {
        // error handled in store
      }
    });
  };

  // --- AGENTS HANDLERS (MULTI-QUEUE ASSIGNMENT) ---
  const handleToggleAgentFormQueue = (qLabel: string) => {
    setAgentForm((prev) => {
      const exists = prev.assigned_queues.includes(qLabel);
      if (exists) {
        if (prev.assigned_queues.length <= 1) {
          toast.error('Agent must belong to at least 1 queue');
          return prev;
        }
        return { ...prev, assigned_queues: prev.assigned_queues.filter((q) => q !== qLabel) };
      } else {
        return { ...prev, assigned_queues: [...prev.assigned_queues, qLabel] };
      }
    });
  };

  const handleToggleEditingAgentQueue = (qLabel: string) => {
    if (!editingAgent) return;
    const currentQueues = editingAgent.assigned_queues || [editingAgent.assigned_queue || 'Support Team'];
    const exists = currentQueues.includes(qLabel);
    let updated: string[];
    if (exists) {
      if (currentQueues.length <= 1) {
        toast.error('Agent must belong to at least 1 queue');
        return;
      }
      updated = currentQueues.filter((q: string) => q !== qLabel);
    } else {
      updated = [...currentQueues, qLabel];
    }
    setEditingAgent({ ...editingAgent, assigned_queues: updated });
  };

  const handleAddAgentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agentForm.first_name || !agentForm.email) return;
    try {
      const selectedQueues = agentForm.assigned_queues.length ? agentForm.assigned_queues : ['Support Team'];
      const matchedQueue = queues.find((q) => selectedQueues.includes(q.queue_label || q.label || q.name || ''));
      const teamKey = matchedQueue?.queue_key || matchedQueue?.key || 'support';
      const teamName = matchedQueue?.queue_label || matchedQueue?.label || 'Support Team';

      await addAgent({
        first_name: agentForm.first_name,
        last_name: agentForm.last_name,
        email: agentForm.email,
        role: agentForm.role || 'Support Agent',
        assigned_queues: selectedQueues,
        assigned_teams: [teamKey],
        status: 'Active',
        team_key: teamKey,
        team_name: teamName,
      });
      setShowAddAgentModal(false);
      setAgentForm({ first_name: '', last_name: '', email: '', role: 'Support Agent', assigned_queues: ['Support Team'] });
      await fetchAgents();
    } catch {
      // error handled in store
    }
  };

  const handleUpdateAgent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAgent) return;
    try {
      const selectedQueues = editingAgent.assigned_queues?.length ? editingAgent.assigned_queues : ['Support Team'];
      const matchedQueue = queues.find((q) => selectedQueues.includes(q.queue_label || q.label || q.name));
      const teamKey = matchedQueue?.queue_key || matchedQueue?.key || editingAgent.team_key || 'support';
      const teamName = matchedQueue?.queue_label || matchedQueue?.label || editingAgent.team_name || 'Support Team';

      await updateAgent(editingAgent.id, {
        first_name: editingAgent.first_name,
        last_name: editingAgent.last_name,
        email: editingAgent.email,
        role: editingAgent.role,
        assigned_queues: selectedQueues,
        assigned_teams: [teamKey],
        team_key: teamKey,
        team_name: teamName,
      });
      setEditingAgent(null);
      await fetchAgents();
    } catch {
      // error handled in store
    }
  };

  const handleDeleteAgent = (id: string, name: string) => {
    triggerConfirmation('Confirm Agent Removal', `Are you sure you want to remove agent "${name}"? Open tickets assigned to this agent will be unassigned.`, async () => {
      await deleteAgent(id);
      await fetchAgents();
    });
  };

  // --- WORKFLOW HANDLERS ---
  const handleAddWorkflowSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!workflowForm.type) return;
    const states = workflowForm.statesStr.split(',').map((s) => s.trim()).filter(Boolean);
    await addWorkflow({
      type: workflowForm.type,
      description: workflowForm.description,
      initial_status: workflowForm.initial_status || states[0] || 'New',
      states,
    });
    setShowAddWorkflowModal(false);
    setWorkflowForm({ type: '', description: '', initial_status: 'New', statesStr: 'New, In Progress, Resolved, Closed' });
  };

  const handleUpdateWorkflow = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingWorkflow) return;
    await updateWorkflow(editingWorkflow.id, editingWorkflow);
    setEditingWorkflow(null);
  };

  const handleDeleteWorkflow = (id: string, type: string) => {
    triggerConfirmation('Confirm Workflow Deletion', `Are you sure you want to delete Ticket Type & Workflow "${type}"? This cannot be undone.`, async () => {
      await deleteWorkflow(id);
    });
  };

  // --- EMAIL CONFIGURATION & GOOGLE MAILING HANDLERS ---
  const handleSaveMailConfig = (e: React.FormEvent) => {
    e.preventDefault();
    const providerName = mailProviderChoice === 'google' 
      ? 'Google Workspace Gmail API' 
      : mailProviderChoice === 'mailu'
      ? 'Mailu Mail Server Stack'
      : 'Outbound SMTP';
    toast.success(`${providerName} Configuration & Security Rules saved!`);
  };

  const handleTestMailuServer = async () => {
    setIsTestingMailu(true);
    setMailuTestResult(null);
    try {
      const res = await testMailuConnection(mailuConfig);
      setMailuTestResult(res);
    } catch (err: any) {
      setMailuTestResult({ success: false, message: err?.message || 'Connection test failed' });
    } finally {
      setIsTestingMailu(false);
    }
  };

  const handleUpdateQueueMailAlias = async (e: React.FormEvent) => {
    e.preventDefault();
    const teamId = editingQueueMail.team_id || editingQueueMail.queue_id || editingQueueMail.id;
    try {
      await apiClient.patch(`/support/teams/${teamId}`, {
        label: editingQueueMail.queue_label,
        emailAddress: editingQueueMail.emailAddress !== undefined ? editingQueueMail.emailAddress.trim() : '',
        email_address: editingQueueMail.emailAddress !== undefined ? editingQueueMail.emailAddress.trim() : '',
        email_routing: editingQueueMail.emailAddress !== undefined ? editingQueueMail.emailAddress.trim() : '',
        serverMode: editingQueueMail.serverMode,
        default_sla: editingQueueMail.default_sla,
        threadMatchingEnabled: editingQueueMail.threadMatchingEnabled,
        dedicatedSmtpHost: editingQueueMail.dedicatedSmtpHost,
        dedicatedSmtpPort: editingQueueMail.dedicatedSmtpPort,
        dedicatedSmtpUser: editingQueueMail.dedicatedSmtpUser,
        dedicatedSmtpPass: editingQueueMail.dedicatedSmtpPass,
        dedicatedSmtpSecurity: editingQueueMail.dedicatedSmtpSecurity,
        dedicatedImapHost: editingQueueMail.dedicatedImapHost,
        dedicatedImapPort: editingQueueMail.dedicatedImapPort,
        dedicatedImapUser: editingQueueMail.dedicatedImapUser,
        dedicatedImapPass: editingQueueMail.dedicatedImapPass,
        dedicatedImapSecurity: editingQueueMail.dedicatedImapSecurity,
      });
      toast.success(`Dedicated Mail Server & Credentials for queue "${editingQueueMail.queue_label || editingQueueMail.label || editingQueueMail.name}" updated!`);
      setEditingQueueMail(null);
      await fetchQueues();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to update mail config');
    }
  };

  const handleRunTestOutboundEmail = () => {
    if (!testEmailRecipient) {
      toast.error('Please enter a test recipient email address');
      return;
    }
    setIsTestingEmail(true);
    const providerName = mailProviderChoice === 'google' ? 'Google Workspace Gmail API' : `SMTP Server (${smtpConfig.host}:${smtpConfig.port})`;
    setTestEmailLogs([`[${new Date().toLocaleTimeString()}] Authenticating via ${providerName}...`]);

    setTimeout(() => {
      setTestEmailLogs((prev) => [...prev, `[${new Date().toLocaleTimeString()}] Verifying DKIM signature & SPF policy alignment... PASSED.`]);
    }, 600);

    setTimeout(() => {
      setTestEmailLogs((prev) => [...prev, `[${new Date().toLocaleTimeString()}] Encrypting TLS payload... Authentication OK.`]);
    }, 1200);

    setTimeout(() => {
      setTestEmailLogs((prev) => [...prev, `[${new Date().toLocaleTimeString()}] Outbound message delivered to ${testEmailRecipient}. 250 2.0.0 OK QueueId: 98124.`]);
      setIsTestingEmail(false);
      toast.success(`Test email sent to ${testEmailRecipient} via ${mailProviderChoice === 'google' ? 'Google API' : 'SMTP'}!`);
    }, 1800);
  };

  const handleRunTestInboundFetch = () => {
    setIsTestingInbound(true);
    const modeName = inboundConfig.mode === 'webhook' ? 'Inbound Webhook' : inboundConfig.mode === 'google_api' ? 'Google Gmail API' : 'POP3 Server';
    setTestEmailLogs([`[${new Date().toLocaleTimeString()}] Testing ${modeName} Listener connection...`]);

    setTimeout(() => {
      setTestEmailLogs((prev) => [...prev, `[${new Date().toLocaleTimeString()}] Authenticating inbound connection token... PASSED.`]);
    }, 700);

    setTimeout(() => {
      setTestEmailLogs((prev) => [...prev, `[${new Date().toLocaleTimeString()}] Scanning inbound queue... 2 incoming emails detected.`]);
    }, 1400);

    setTimeout(() => {
      setTestEmailLogs((prev) => [...prev, `[${new Date().toLocaleTimeString()}] Parsed body & metadata. Created Ticket #9014 under target queue.`]);
      setIsTestingInbound(false);
      toast.success(`Inbound Email Fetcher (${modeName}) Connection Test Passed!`);
    }, 2100);
  };

  const handleAddTemplateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTemplateForm.name || !newTemplateForm.subject) return;
    const newTpl = {
      id: `tpl-${Date.now()}`,
      name: newTemplateForm.name,
      category: newTemplateForm.category || 'notification',
      subject: newTemplateForm.subject,
      body: newTemplateForm.body || 'Dear {client_name},\n\nThank you for reaching out to support.\n\nBest regards,\n{company_name}',
    };
    setTemplates([...templates, newTpl]);
    setSelectedTemplateId(newTpl.id);
    toast.success(`Email template "${newTemplateForm.name}" created successfully!`);
    setShowAddTemplateModal(false);
    setNewTemplateForm({ name: '', category: 'notification', subject: '', body: '' });
  };

  const handleDeleteTemplate = (id: string, name: string) => {
    if (templates.length <= 1) {
      toast.error('You must keep at least 1 email template');
      return;
    }
    triggerConfirmation('Confirm Template Deletion', `Are you sure you want to delete template "${name}"? This action cannot be undone.`, () => {
      const filtered = templates.filter((t) => t.id !== id);
      setTemplates(filtered);
      if (selectedTemplateId === id) {
        setSelectedTemplateId(filtered[0]?.id || '');
      }
      toast.success(`Template "${name}" deleted successfully!`);
    });
  };

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId) || templates[0];

  return (
    <div className="min-h-full space-y-4 overflow-x-hidden bg-background p-4 pb-24 font-sans text-xs text-foreground">
      {/* 2-COLUMN ENTERPRISE LAYOUT: LEFT VERTICAL NAV + RIGHT WORKSPACE */}
      <div className="flex flex-col lg:flex-row gap-5 items-start">
        {/* 1. LEFT VERTICAL NAVIGATION (Grouped into Tickets, People, Channels, Data, Knowledge Base) */}
        <aside className="w-full lg:w-64 shrink-0 rounded-xl border border-border bg-card p-3 shadow-xs space-y-4 lg:sticky lg:top-4">
          <div className="flex items-center gap-2.5 pb-3 border-b border-border">
            <div className="size-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
              <Settings className="size-4" />
            </div>
            <div>
              <h2 className="font-display font-semibold text-sm text-foreground">Support Settings</h2>
              <p className="text-[11px] text-muted-foreground">Single enterprise control panel</p>
            </div>
          </div>

          <nav className="space-y-4" aria-label="Support Settings Navigation">
            {SETTINGS_NAV_GROUPS.map((group) => (
              <div key={group.label} className="space-y-1">
                <div className="px-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {group.label}
                </div>
                <div className="space-y-0.5">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeTab === item.id;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => selectTab(item.id)}
                        className={cn(
                          "w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg text-xs font-medium transition-colors text-left cursor-pointer",
                          isActive
                            ? "bg-primary text-primary-foreground font-semibold shadow-xs"
                            : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                        )}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <Icon className={cn("size-3.5 shrink-0", isActive ? "text-primary-foreground" : "text-muted-foreground")} />
                          <span className="truncate">{item.label}</span>
                        </div>
                        {item.badge && (
                          <span className={cn(
                            "px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider",
                            isActive
                              ? "bg-primary-foreground/20 text-primary-foreground"
                              : "bg-primary/10 text-primary"
                          )}>
                            {item.badge}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>
        </aside>

        {/* 2. RIGHT WORKSPACE */}
        <main className="flex-1 min-w-0 space-y-4 w-full">
          {/* TOP CONTEXTUAL HEADER CARD */}
          <div className="p-4 rounded-xl border border-border bg-card shadow-xs flex flex-col sm:flex-row justify-between sm:items-center gap-3 shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              {activeTabMeta?.icon && (
                <div className="size-9 rounded-lg bg-primary/10 border border-primary/20 text-primary flex items-center justify-center font-bold shrink-0">
                  <activeTabMeta.icon className="size-4.5" />
                </div>
              )}
              <div className="space-y-0.5 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="font-display font-semibold text-page-title tracking-tight text-foreground truncate">
                    {activeTabLabel}
                  </h1>
                  {activeTab === 'kb_settings' && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-primary/10 text-primary border border-primary/20">
                      Beta
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  {activeTabDescription}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0 flex-wrap">
              {/* Tab-specific actions in header card */}
              {activeTab === 'queues' && (
                <button
                  type="button"
                  onClick={() => {
                    setNewTeamForm({
                      key: '', label: '', email: '', description: '', defaultSla: 'Default SLA Policy',
                      serverMode: 'primary_system', threadMatchingEnabled: true,
                      dedicatedSmtpHost: '', dedicatedSmtpPort: '587', dedicatedSmtpUser: '', dedicatedSmtpPass: '', dedicatedSmtpSecurity: 'TLS',
                      dedicatedImapHost: '', dedicatedImapPort: '993', dedicatedImapUser: '', dedicatedImapPass: '', dedicatedImapSecurity: 'SSL',
                      assignedAgentIds: [],
                    });
                    setShowAddTeamModal(true);
                  }}
                  className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-primary text-primary-foreground px-3 text-xs font-semibold hover:bg-primary/90 transition-colors shadow-xs cursor-pointer"
                >
                  <Plus className="size-3.5" />
                  <span>Add team</span>
                </button>
              )}

              {activeTab === 'ai_chat' && (
                <>
                  <button
                    type="button"
                    disabled={isTestingAi}
                    onClick={handleTestAi}
                    className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-border bg-card px-3 text-xs font-semibold text-foreground hover:bg-secondary transition-colors disabled:opacity-50 cursor-pointer"
                  >
                    <RefreshCw className={cn("size-3.5", isTestingAi && "animate-spin text-primary")} />
                    <span>{isTestingAi ? 'Testing Ping...' : 'Test Connection'}</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveAiSettings}
                    className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-primary text-primary-foreground px-3.5 text-xs font-semibold hover:bg-primary/90 transition-colors shadow-xs cursor-pointer"
                  >
                    <Save className="size-3.5" />
                    <span>Save AI Settings</span>
                  </button>
                </>
              )}



              {activeTab === 'kb_settings' && (
                <button
                  type="button"
                  onClick={handleSaveKbSettings}
                  className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-primary text-primary-foreground px-3.5 text-xs font-semibold hover:bg-primary/90 transition-colors shadow-xs cursor-pointer"
                >
                  <Save className="size-3.5" />
                  <span>Save KB Settings</span>
                </button>
              )}

              {/* Sub-view registered actions (e.g. Agents & Access) */}
              {headerActions}

              {/* Always display Sync Settings button unless already on agents which has custom refresh */}
              {activeTab !== 'agents' && (
                <button
                  type="button"
                  onClick={() => {
                    fetchQueues();
                    fetchAgents();
                    fetchStorageConfig();
                    fetchSlaPolicies();
                    toast.success('Settings synchronized with database');
                  }}
                  className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-border bg-card px-3 text-xs font-semibold text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 cursor-pointer"
                >
                  <RefreshCw className="size-3.5 text-muted-foreground" />
                  <span>Sync Settings</span>
                </button>
              )}
            </div>
          </div>

          {/* TAB CONTENTS */}
          <div id="support-settings-panel" role="tabpanel" className="space-y-4" tabIndex={0}>
        {/* SLA POLICY & CONFIGURATION */}
        {activeTab === 'sla' && <SLATrackingView />}

        {/* 1. TEAM MANAGEMENT */}
        {activeTab === 'queues' && (
          <div className="space-y-5">
            <div className="rounded-xl border border-border bg-card shadow-xs overflow-hidden">
              <div className="p-3.5 border-b border-border bg-card flex items-center justify-between gap-3 flex-wrap">
                <span className="text-xs font-semibold text-foreground">
                  Configured Support Teams ({queues.length})
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse">
                  <thead className="border-b border-border bg-card/60 text-xs font-semibold text-muted-foreground">
                    <tr>
                      <th className="py-3 px-4">Team Key</th>
                      <th className="py-3 px-4">Team Name / Label</th>
                      <th className="py-3 px-4">Team Email</th>
                      <th className="py-3 px-4">Mail Server Mode</th>
                      <th className="py-3 px-4">Default SLA Policy</th>
                      <th className="py-3 px-4 text-center">Active Tickets</th>
                      <th className="py-3 px-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {queues.map((q) => {
                      const qId = q.queue_id || q.id || q.team_id;
                      const qKey = q.queue_key || q.key || q.team_key;
                      const qLabel = q.queue_label || q.label || q.team_name || q.name;
                      const qEmail = q.emailAddress || q.email_address || q.email_routing;
                      const isDedicatedSmtp = (q.serverMode || q.server_mode) === 'dedicated_smtp';
                      const isDedicatedGoogle = (q.serverMode || q.server_mode) === 'dedicated_google';
                      return (
                        <tr
                          key={qKey || qId}
                          onClick={() => openEditTeamModal(q)}
                          className="h-12 cursor-pointer transition-colors hover:bg-accent/40"
                        >
                          <td className="py-3 px-4 font-mono font-semibold text-foreground">{qKey || '—'}</td>
                          <td className="py-3 px-4 font-semibold text-foreground">{qLabel}</td>
                          <td className="py-3 px-4">
                            {(() => {
                              const dedicated = ((q.configuredEmail || q.configured_email || '').trim());
                              const actualMail = dedicated || (qEmail ? qEmail.trim() : '');
                              if (actualMail) {
                                return (
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono font-semibold text-foreground">
                                      {actualMail}
                                    </span>
                                    {dedicated ? (
                                      <span className="inline-flex items-center gap-1 rounded bg-blue-50 dark:bg-blue-950/50 px-1.5 py-0.5 text-[10px] font-semibold text-primary border border-primary/20">
                                        Dedicated
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1 rounded bg-secondary px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground border border-border">
                                        Mail Infrastructure
                                      </span>
                                    )}
                                  </div>
                                );
                              }
                              return (
                                <span className="inline-flex items-center gap-1.5 rounded bg-muted/60 px-2 py-0.5 text-[11px] text-muted-foreground border border-border/60 font-medium">
                                  Mail not configured
                                </span>
                              );
                            })()}
                          </td>
                          <td className="py-3 px-4">
                            {isDedicatedSmtp ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300 border border-blue-500/30">
                                Dedicated Custom SMTP &amp; IMAP Service
                              </span>
                            ) : isDedicatedGoogle ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300 border border-purple-500/30">
                                Dedicated Google OAuth
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-muted text-muted-foreground border border-border">
                                Inherit System Primary Server
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-muted-foreground">{q.default_sla || q.defaultSla || 'Default SLA Policy'}</td>
                          <td className="py-3 px-4 text-center">
                            <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-muted px-2 font-semibold text-foreground">
                              {q.active_tickets ?? q.active_tickets_count ?? '—'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right" onClick={(event) => event.stopPropagation()}>
                            <button
                              type="button"
                              onClick={() => openEditTeamModal(q)}
                              className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                              aria-label={`View ${qLabel} details`}
                            >
                              <ArrowRight className="size-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* 2. AGENTS & ACCESS CONTROL */}
        {activeTab === 'agents' && <AgentsAndAccessView onRegisterHeaderActions={setHeaderActions} />}

        {/* 3. WORKFLOWS & FLOWCHART VISUALIZER */}
        {activeTab === 'workflows' && <WorkflowsView />}

        {/* 3b. TICKET TYPES & FIELDS DESIGNER */}
        {activeTab === 'ticket_types' && <TicketTypesView />}

        {/* 3c. QUICK ACTIONS & TRIGGERS */}
        {activeTab === 'actions' && <ActionsView />}

        {/* 5. EMAIL CONFIGURATION, GOOGLE MAILING & QUEUE MAILBOX MAPPING */}
        {activeTab === 'email_config' && <MailInfrastructureTab />}

        {/* 6. EMAIL & SIGNATURE TEMPLATES & GUI CARD BUILDER */}
        {activeTab === 'templates' && (
          <EmailTemplatesBuilderView />
        )}
  {isDirty && (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center justify-between gap-4 rounded-xl border border-border bg-card px-5 py-3 shadow-2xl backdrop-blur-md animate-in slide-in-from-bottom duration-200">
      <div className="flex items-center gap-2">
        <AlertCircle className="size-4 text-amber-500" />
        <span className="text-xs font-semibold text-foreground">
          Unsaved changes in {activeTabLabel}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleDiscardActive}
          className="rounded-md border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-secondary transition-colors cursor-pointer"
        >
          Discard
        </button>
        <button
          type="button"
          onClick={handleSaveActive}
          className="rounded-md bg-primary text-primary-foreground px-4 py-1.5 text-xs font-semibold hover:bg-primary/90 transition-colors shadow-xs cursor-pointer flex items-center gap-1.5"
        >
          <Save className="size-3.5" /> Save Changes
        </button>
      </div>
    </div>
  )}

      {/* CLOUD STORAGE (AWS S3 VAULT) TAB */}
      {activeTab === 'storage' && <CloudStorageTab />}

      {/* AI & LIVE CHAT COPILOT SETTINGS */}
      {activeTab === 'ai_chat' && (
        <div className="space-y-5">
          <div className="rounded-xl border border-border bg-card p-4 shadow-xs space-y-4">

            {aiTestResult && (
              <div className={cn(
                "p-3 rounded-lg border text-xs flex items-center justify-between gap-3",
                aiTestResult.success
                  ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-800 dark:text-emerald-300"
                  : "bg-destructive/10 border-destructive/20 text-destructive"
              )}>
                <div className="flex items-center gap-2">
                  {aiTestResult.success ? <CheckCircle2 className="size-4 text-emerald-600" /> : <AlertTriangle className="size-4 text-destructive" />}
                  <span className="font-medium">{aiTestResult.message}</span>
                </div>
                {aiTestResult.latency && (
                  <span className="font-mono text-xs opacity-80">{aiTestResult.latency} ms</span>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="font-semibold text-foreground text-xs block mb-1">AI Provider Engine</label>
                <select
                  value={aiSettings.provider}
                  onChange={(e) => setAiSettings({ ...aiSettings, provider: e.target.value as any })}
                  className="h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                >
                  <option value="ollama">Ollama (Open-Source Local Server)</option>
                  <option value="openai">OpenAI Compatible API</option>
                  <option value="custom">Custom Internal Gateway</option>
                </select>
                <p className="text-[11px] text-muted-foreground mt-1">Select local open-source server or external model provider.</p>
              </div>

              <div>
                <label className="font-semibold text-foreground text-xs block mb-1">API Endpoint URL</label>
                <input
                  type="text"
                  value={aiSettings.api_url}
                  onChange={(e) => setAiSettings({ ...aiSettings, api_url: e.target.value })}
                  placeholder="http://localhost:11434"
                  className="h-8 w-full rounded-md border border-input bg-background px-2.5 font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                />
                <p className="text-[11px] text-muted-foreground mt-1">Host endpoint where Ollama or model gateway is listening.</p>
              </div>

              <div>
                <label className="font-semibold text-foreground text-xs block mb-1">Chat Generation Model</label>
                <input
                  type="text"
                  value={aiSettings.chat_model}
                  onChange={(e) => setAiSettings({ ...aiSettings, chat_model: e.target.value })}
                  placeholder="llama3"
                  className="h-8 w-full rounded-md border border-input bg-background px-2.5 font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                />
                <p className="text-[11px] text-muted-foreground mt-1">e.g. llama3, mistral, qwen2.5, gpt-4o-mini</p>
              </div>

              <div>
                <label className="font-semibold text-foreground text-xs block mb-1">Embedding Model (RAG Knowledge Base)</label>
                <input
                  type="text"
                  value={aiSettings.embedding_model}
                  onChange={(e) => setAiSettings({ ...aiSettings, embedding_model: e.target.value })}
                  placeholder="nomic-embed-text"
                  className="h-8 w-full rounded-md border border-input bg-background px-2.5 font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                />
                <p className="text-[11px] text-muted-foreground mt-1">e.g. nomic-embed-text, bge-small-en-v1.5</p>
              </div>

              <div>
                <label className="font-semibold text-foreground text-xs block mb-1">
                  Confidence Threshold ({Math.round(aiSettings.confidence_threshold * 100)}%)
                </label>
                <input
                  type="range"
                  min="0.1"
                  max="1.0"
                  step="0.05"
                  value={aiSettings.confidence_threshold}
                  onChange={(e) => setAiSettings({ ...aiSettings, confidence_threshold: parseFloat(e.target.value) })}
                  className="w-full accent-primary"
                />
                <p className="text-[11px] text-muted-foreground mt-1">Answers below this score automatically offer to escalate or create a ticket.</p>
              </div>

              <div>
                <label className="font-semibold text-foreground text-xs block mb-1">Max KB Articles in Prompt Context</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={aiSettings.max_context_articles}
                  onChange={(e) => setAiSettings({ ...aiSettings, max_context_articles: parseInt(e.target.value, 10) || 3 })}
                  className="h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                />
                <p className="text-[11px] text-muted-foreground mt-1">Number of top relevant knowledge articles injected into LLM prompt.</p>
              </div>
            </div>

            <div className="space-y-3 pt-3 border-t border-border">
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-semibold text-foreground text-xs block">AI Copilot Active on Live Chat</span>
                  <span className="text-[11px] text-muted-foreground">When enabled, AI automatically responds first to customer inquiries before human routing.</span>
                </div>
                <input
                  type="checkbox"
                  checked={aiSettings.enabled}
                  onChange={(e) => setAiSettings({ ...aiSettings, enabled: e.target.checked })}
                  className="size-4 accent-primary rounded cursor-pointer"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <span className="font-semibold text-foreground text-xs block">Auto-Ticket on Low Confidence Handoff</span>
                  <span className="text-[11px] text-muted-foreground">Create an unresolved support ticket automatically when AI confidence is below threshold.</span>
                </div>
                <input
                  type="checkbox"
                  checked={aiSettings.auto_handoff_low_confidence}
                  onChange={(e) => setAiSettings({ ...aiSettings, auto_handoff_low_confidence: e.target.checked })}
                  className="size-4 accent-primary rounded cursor-pointer"
                />
              </div>
            </div>

            <div className="space-y-1.5 pt-3 border-t border-border">
              <label className="font-semibold text-foreground text-xs block">System Instruction Prompt</label>
              <textarea
                rows={3}
                value={aiSettings.system_prompt}
                onChange={(e) => setAiSettings({ ...aiSettings, system_prompt: e.target.value })}
                className="w-full rounded-md border border-input bg-background p-2.5 font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
              />
              <p className="text-[11px] text-muted-foreground">Custom baseline prompt injected before knowledge base contexts.</p>
            </div>
          </div>
        </div>
      )}



      {/* KNOWLEDGE BASE SETTINGS (BETA) */}
      {activeTab === 'kb_settings' && (
        <div className="space-y-5">
          <div className="rounded-xl border border-border bg-card p-4 shadow-xs space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-end gap-3 border-b border-border pb-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={isReindexingKb}
                  onClick={handleReindexKb}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-secondary transition-colors disabled:opacity-50 cursor-pointer"
                >
                  <RefreshCw className={cn("size-3.5", isReindexingKb && "animate-spin text-primary")} />
                  {isReindexingKb ? 'Re-indexing...' : 'Re-index Vector Embeddings'}
                </button>
                <button
                  type="button"
                  onClick={handleSaveKbSettings}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-primary text-primary-foreground px-3.5 py-1.5 text-xs font-semibold hover:bg-primary/90 transition-colors shadow-xs cursor-pointer"
                >
                  <Save className="size-3.5" /> Save Changes
                </button>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-muted/20 border border-border rounded-lg">
                <div>
                  <span className="font-semibold text-foreground text-xs block">Knowledge Base Enabled (Global Feature Flag)</span>
                  <span className="text-[11px] text-muted-foreground">
                    When toggled off, customer portal and agents see a Beta Under Maintenance placeholder.
                  </span>
                </div>
                <input
                  type="checkbox"
                  checked={kbEnabled}
                  onChange={(e) => setKbEnabled(e.target.checked)}
                  className="size-4 accent-primary rounded cursor-pointer"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="p-3 bg-muted/20 border border-border rounded-lg space-y-1">
                  <span className="text-xs font-semibold text-foreground">AI Ticket Deflection Target</span>
                  <p className="text-[11px] text-muted-foreground">
                    Published articles are automatically embedded into 1000-character chunks with 200-char overlap for RAG similarity search.
                  </p>
                </div>
                <div className="p-3 bg-muted/20 border border-border rounded-lg space-y-1">
                  <span className="text-xs font-semibold text-foreground">Beta Feedback Email</span>
                  <p className="text-[11px] font-mono text-muted-foreground">
                    support@trustybytes.in
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* WEBHOOKS & CHAT ALERTS TAB */}
      {mainTab === 'webhooks' && webhookSubTab === 'webhooks' && (
        <div className="space-y-6">
          {/* TOP OVERVIEW CARD WITH MULTI-WEBHOOK PRESET ACTIONS */}
            <div className="rounded-xl border border-border bg-card p-4 shadow-xs space-y-4">
              <div className="flex flex-col sm:flex-row items-end sm:items-center justify-end gap-4 pb-3 border-b border-border">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs font-semibold">
                    <CheckCircle2 className="size-3.5" />
                    Engine Active ({webhooks.filter((w) => w.isActive).length} / {webhooks.length} Online)
                  </span>

                  {!isWebhooksEditMode ? (
                    <>
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-muted text-muted-foreground border border-border text-xs font-semibold">
                        <Lock className="size-3 text-muted-foreground" /> Read-only
                      </span>
                      <button
                        type="button"
                        onClick={() => setIsWebhooksEditMode(true)}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-primary text-primary-foreground px-3.5 py-1.5 text-xs font-semibold shadow-xs hover:bg-primary/90 transition-all cursor-pointer"
                      >
                        <Edit className="size-3.5" /> Edit
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20 text-xs font-semibold animate-pulse">
                        <Edit className="size-3" /> Editing
                      </span>
                      <button
                        type="button"
                        onClick={() => setIsWebhooksEditMode(false)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-accent transition-colors cursor-pointer"
                      >
                        <X className="size-3.5" /> Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsWebhooksEditMode(false);
                          toast.success('Notification & Webhook configurations applied and saved');
                        }}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-primary text-primary-foreground px-4 py-1.5 text-xs font-semibold shadow-xs hover:bg-primary/90 transition-all cursor-pointer"
                      >
                        <Check className="size-3.5" /> Apply &amp; Save
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* QUICK MULTI-WEBHOOK PRESET BUTTONS */}
              <div className="pt-3 border-t border-border flex flex-wrap items-center gap-2 text-xs">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1 mr-1">
                  <Sparkles className="size-3 text-amber-500" /> Multi-Webhook Presets:
                </span>
                <button
                  type="button"
                  onClick={() => applyWebhookPreset('tickets')}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-sky-50 dark:bg-sky-950/40 border border-sky-200 dark:border-sky-800 text-sky-700 dark:text-sky-300 hover:bg-sky-100 text-xs font-semibold transition-colors"
                >
                  <Layers className="size-3 text-sky-600" /> + Tickets Feed
                </button>
                <button
                  type="button"
                  onClick={() => applyWebhookPreset('sla')}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 hover:bg-rose-100 text-xs font-semibold transition-colors"
                >
                  <AlertTriangle className="size-3 text-rose-600" /> + SLA Escalations Space
                </button>
                <button
                  type="button"
                  onClick={() => applyWebhookPreset('reports')}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-purple-50 dark:bg-purple-950/40 border border-purple-200 dark:border-purple-800 text-purple-700 dark:text-purple-300 hover:bg-purple-100 text-xs font-semibold transition-colors"
                >
                  <FileText className="size-3 text-purple-600" /> + Executive Reports Feed
                </button>
                <button
                  type="button"
                  onClick={() => applyWebhookPreset('assets')}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 text-xs font-semibold transition-colors"
                >
                  <HardDrive className="size-3 text-emerald-600" /> + IT Assets Channel
                </button>
                <button
                  type="button"
                  onClick={() => applyWebhookPreset('finance')}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300 hover:bg-amber-100 text-xs font-semibold transition-colors"
                >
                  <Tag className="size-3 text-amber-600" /> + Overdue Invoices
                </button>
              </div>
            </div>

            {/* MAIN TWO-COLUMN WORKSPACE: CONFIGURATION + PREVIEW/SIMULATION */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* LEFT COLUMN: REGISTRATION / EDIT FORM */}
              <div className="lg:col-span-6 space-y-6">
                <div className="rounded-xl border border-border bg-card p-5 shadow-xs space-y-4">
                  <div className="flex items-center justify-between border-b border-border pb-3">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground flex items-center gap-2">
                      <Zap className="size-4 text-primary" /> {editingWebhook ? 'Edit Webhook Integration' : 'Register New Webhook / Chat Space'}
                    </h3>
                    {editingWebhook && (
                      <button
                        type="button"
                        onClick={() => {
                          setEditingWebhook(null);
                          setNewWebhookForm({
                            name: '',
                            url: '',
                            platform: 'google_chat',
                            direction: 'outbound',
                            enableInbound: false,
                            googleSpaceId: '',
                            googleApiKey: '',
                            googleApiToken: '',
                            events: ['ticket.created', 'ticket.status_changed', 'ticket.sla_breach', 'ticket.resolved', 'report.daily_summary', 'asset.warranty_expiring'],
                            secret: '',
                            description: '',
                          });
                        }}
                        className="text-xs text-muted-foreground hover:text-foreground font-medium"
                      >
                        Cancel Edit
                      </button>
                    )}
                  </div>

                  <form
                    onSubmit={async (e) => {
                      e.preventDefault();
                      if (!newWebhookForm.name || !newWebhookForm.url) {
                        toast.error('Webhook Name and Target URL are required');
                        return;
                      }
                      if (editingWebhook) {
                        await updateWebhook(editingWebhook.id, {
                          name: newWebhookForm.name,
                          url: newWebhookForm.url,
                          platform: newWebhookForm.platform,
                          direction: newWebhookForm.direction,
                          enableInbound: newWebhookForm.enableInbound,
                          googleSpaceId: newWebhookForm.googleSpaceId,
                          googleApiKey: newWebhookForm.googleApiKey,
                          googleApiToken: newWebhookForm.googleApiToken,
                          events: newWebhookForm.events,
                          secret: newWebhookForm.secret,
                          description: newWebhookForm.description,
                        });
                        setEditingWebhook(null);
                      } else {
                        await addWebhook({
                          name: newWebhookForm.name,
                          url: newWebhookForm.url,
                          platform: newWebhookForm.platform,
                          direction: newWebhookForm.direction,
                          enableInbound: newWebhookForm.enableInbound,
                          googleSpaceId: newWebhookForm.googleSpaceId,
                          googleApiKey: newWebhookForm.googleApiKey,
                          googleApiToken: newWebhookForm.googleApiToken,
                          events: newWebhookForm.events,
                          secret: newWebhookForm.secret,
                          description: newWebhookForm.description,
                          isActive: true,
                        });
                      }
                      setNewWebhookForm({
                        name: '',
                        url: '',
                        platform: 'google_chat',
                        direction: 'outbound',
                        enableInbound: false,
                        googleSpaceId: '',
                        googleApiKey: '',
                        googleApiToken: '',
                        events: ['ticket.created', 'ticket.status_changed', 'ticket.sla_breach', 'ticket.resolved', 'report.daily_summary', 'asset.warranty_expiring'],
                        secret: '',
                        description: '',
                      });
                    }}
                    className="space-y-4 text-xs"
                  >
                    <div>
                      <label className="block font-semibold text-foreground mb-1">Webhook Name &amp; Dedicated Channel Purpose *</label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. IT Helpdesk Space, Ops SLA Escalations, or Weekly Reports Briefing"
                        value={newWebhookForm.name}
                        onChange={(e) => setNewWebhookForm({ ...newWebhookForm, name: e.target.value })}
                        className="w-full h-8 px-3 rounded-md border border-input bg-background font-medium text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block font-semibold text-foreground mb-1">Target Platform *</label>
                        <select
                          value={newWebhookForm.platform}
                          onChange={(e) => {
                            const newPlat = e.target.value as any;
                            setNewWebhookForm({ ...newWebhookForm, platform: newPlat });
                          }}
                          className="w-full h-8 px-2.5 rounded-md border border-input bg-background text-xs font-semibold text-foreground outline-none focus:ring-1 focus:ring-ring"
                        >
                          <option value="google_chat">Google Workspace Chat Space (cardsV2)</option>
                          <option value="slack">Slack / Discord / Microsoft Teams</option>
                          <option value="generic">Generic JSON Webhook (HMAC-SHA256)</option>
                          <option value="email">Email Notification Endpoint</option>
                        </select>
                      </div>

                      <div>
                        <label className="block font-semibold text-foreground mb-1">Webhook Secret / Signing Key</label>
                        <div className="flex gap-1.5">
                          <input
                            type="password"
                            placeholder="HMAC Secret Key"
                            value={newWebhookForm.secret}
                            onChange={(e) => setNewWebhookForm({ ...newWebhookForm, secret: e.target.value })}
                            className="flex-1 h-8 px-3 rounded-md border border-input bg-background font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const gen = generateRandomHexToken(32);
                              setNewWebhookForm({ ...newWebhookForm, secret: gen });
                              toast.success('Generated new secure 32-byte secret');
                            }}
                            className="px-2.5 h-8 rounded border border-border bg-muted hover:bg-accent text-foreground text-xs font-semibold shrink-0"
                            title="Generate Secret"
                          >
                            <Key className="size-3" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* DEDICATED GOOGLE WORKSPACE CHAT SPACES KEY & TOKEN BUILDER */}
                    {newWebhookForm.platform === 'google_chat' && (
                      <div className="p-3.5 bg-sky-50/50 dark:bg-sky-950/30 rounded-lg border border-sky-200 dark:border-sky-800/60 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-xs text-sky-900 dark:text-sky-200 flex items-center gap-1.5">
                            <Bot className="size-3.5 text-primary" /> Google Chat Spaces Integration &amp; URL Helper
                          </span>
                        </div>

                        {/* STEP-BY-STEP GUIDANCE BANNER */}
                        <div className="p-2.5 bg-background/80 rounded border border-border text-xs space-y-1">
                          <p className="font-semibold text-foreground text-xs flex items-center gap-1">
                            <Info className="size-3 text-primary" /> How to connect to a live Google Workspace Chat Space:
                          </p>
                          <ol className="list-decimal list-inside text-muted-foreground space-y-0.5 pl-1">
                            <li>Open your space in <b>Google Chat</b> (<a href="https://chat.google.com" target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-0.5">chat.google.com <ExternalLink className="size-2.5" /></a>).</li>
                            <li>Click the Space name at the top &gt; <b>Apps &amp; integrations</b> &gt; <b>Webhooks</b>.</li>
                            <li>Click <b>Add Webhook</b>, name it <i>&quot;TeamOne Alerts&quot;</i>, and click <b>Save</b>.</li>
                            <li>Click <b>Copy URL</b> and paste it directly into the <b>Target Webhook URL</b> field below.</li>
                          </ol>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          <div>
                            <label className="block text-xs font-semibold text-muted-foreground mb-0.5">Space ID (e.g. AAAAxxxxxx)</label>
                            <input
                              type="text"
                              placeholder="AAAAxxxxxx"
                              value={newWebhookForm.googleSpaceId}
                              onChange={(e) => setNewWebhookForm({ ...newWebhookForm, googleSpaceId: e.target.value })}
                              className="w-full h-7 px-2 rounded border border-border bg-background font-mono text-xs text-foreground outline-none"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-muted-foreground mb-0.5">API Key (key=...)</label>
                            <input
                              type="text"
                              placeholder="AIzaSy..."
                              value={newWebhookForm.googleApiKey}
                              onChange={(e) => setNewWebhookForm({ ...newWebhookForm, googleApiKey: e.target.value })}
                              className="w-full h-7 px-2 rounded border border-border bg-background font-mono text-xs text-foreground outline-none"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-muted-foreground mb-0.5">Security Token</label>
                            <input
                              type="text"
                              placeholder="Google security token"
                              value={newWebhookForm.googleApiToken}
                              onChange={(e) => setNewWebhookForm({ ...newWebhookForm, googleApiToken: e.target.value })}
                              className="w-full h-7 px-2 rounded border border-border bg-background font-mono text-xs text-foreground outline-none"
                            />
                          </div>
                        </div>

                        <div className="flex items-center justify-between pt-1">
                          <p className="text-xs text-muted-foreground">
                            Google Chat generates authentic keys &amp; tokens automatically when you add a webhook to your space.
                          </p>
                          <button
                            type="button"
                            onClick={handleBuildGoogleChatUrl}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-foreground text-background font-semibold text-xs hover:bg-foreground/90 transition-all shadow-xs"
                          >
                            <Zap className="size-3" /> Assemble Webhook URL
                          </button>
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block font-semibold text-foreground mb-1">Target Outbound Webhook URL *</label>
                      <input
                        type="url"
                        required
                        placeholder={
                          newWebhookForm.platform === 'google_chat'
                            ? 'https://chat.googleapis.com/v1/spaces/SPACE_ID/messages?key=KEY&token=TOKEN'
                            : 'https://hooks.slack.com/services/...'
                        }
                        value={newWebhookForm.url}
                        onChange={(e) => handleParseGoogleChatUrl(e.target.value)}
                        className="w-full h-8 px-3 rounded-md border border-input bg-background font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Pasting a full Google Chat webhook URL above will automatically parse Space ID, Key, and Token credentials.
                      </p>
                    </div>

                    <div>
                      <label className="block font-semibold text-foreground mb-1">Description / Notes</label>
                      <input
                        type="text"
                        placeholder="e.g. Dedicated Google Chat room for production outages & daily summaries"
                        value={newWebhookForm.description}
                        onChange={(e) => setNewWebhookForm({ ...newWebhookForm, description: e.target.value })}
                        className="w-full h-8 px-3 rounded-md border border-input bg-background text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                      />
                    </div>

                    {/* OPTIONAL INBOUND WEBHOOK RECEIVER TOGGLE */}
                    <div className="p-3 bg-muted/40 rounded-lg border border-border space-y-2.5">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newWebhookForm.enableInbound}
                          onChange={(e) => setNewWebhookForm({ ...newWebhookForm, enableInbound: e.target.checked })}
                          className="rounded border-input text-foreground focus:ring-0"
                        />
                        <span className="font-semibold text-foreground text-xs">
                          Enable Inbound Webhook Receiver (Optional)
                        </span>
                      </label>
                      <p className="text-xs text-muted-foreground pl-5">
                        Allow external third-party systems, CI/CD pipelines, or monitoring tools to push events into TeamOne via HTTP POST.
                      </p>

                      {newWebhookForm.enableInbound && (
                        <div className="mt-2 pl-5 space-y-2 border-l-2 border-primary/40 text-xs">
                          <div>
                            <span className="text-muted-foreground block font-semibold mb-0.5">TeamOne Inbound Endpoint URL:</span>
                            <div className="flex items-center gap-1.5">
                              <code className="px-2 py-1 bg-background rounded border border-border font-mono text-foreground flex-1 break-all select-all">
                                {inboundEndpointUrl}
                              </code>
                              <button
                                type="button"
                                onClick={() => {
                                  navigator.clipboard.writeText(inboundEndpointUrl);
                                  toast.success('Inbound URL copied to clipboard');
                                }}
                                className="px-2 py-1 rounded bg-muted hover:bg-accent border border-border text-foreground text-xs"
                              >
                                <Copy className="size-3" />
                              </button>
                            </div>
                          </div>
                          <p className="text-muted-foreground text-xs">
                            External callers must supply HMAC signature in <code className="font-mono bg-muted px-1 py-0.5 rounded">X-Webhook-Signature</code> or Bearer token matching the secret key.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* EVENT SUBSCRIPTIONS CHECKLIST */}
                    <div className="space-y-2 pt-2 border-t border-border">
                      <div className="flex items-center justify-between">
                        <label className="font-semibold text-foreground">Subscribed Event Triggers (Outbound)</label>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setNewWebhookForm({
                                ...newWebhookForm,
                                events: [
                                  'ticket.created', 'ticket.status_changed', 'ticket.sla_breach', 'ticket.sla_warning', 'ticket.resolved', 'ticket.note_added',
                                  'asset.status_changed', 'asset.warranty_expiring', 'asset.maintenance_due', 'asset.created',
                                  'invoice.created', 'invoice.paid', 'invoice.overdue',
                                  'report.daily_summary', 'report.support_summary', 'report.assets_summary', 'report.sla_performance'
                                ]
                              });
                            }}
                            className="text-xs text-primary hover:underline font-semibold"
                          >
                            Select All
                          </button>
                          <span className="text-muted-foreground">•</span>
                          <button
                            type="button"
                            onClick={() => setNewWebhookForm({ ...newWebhookForm, events: [] })}
                            className="text-xs text-muted-foreground hover:text-foreground font-semibold"
                          >
                            Clear All
                          </button>
                        </div>
                      </div>

                      <div className="space-y-3 p-3 bg-muted/40 rounded-lg border border-border">
                        {/* TICKETS CATEGORY */}
                        <div>
                          <p className="text-xs font-semibold text-foreground mb-1.5 flex items-center gap-1.5">
                            <Layers className="size-3 text-primary" /> Support &amp; Helpdesk Tickets
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                            {[
                              { id: 'ticket.created', label: 'Ticket Created' },
                              { id: 'ticket.status_changed', label: 'Status Changed' },
                              { id: 'ticket.sla_breach', label: 'SLA Target Breached' },
                              { id: 'ticket.sla_warning', label: 'SLA 30-Min Warning' },
                              { id: 'ticket.resolved', label: 'Ticket Resolved' },
                              { id: 'ticket.note_added', label: 'Note / Reply Added' },
                            ].map((evt) => (
                              <label key={evt.id} className="flex items-center gap-2 cursor-pointer text-xs p-1 hover:bg-card rounded">
                                <input
                                  type="checkbox"
                                  checked={newWebhookForm.events.includes(evt.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setNewWebhookForm({ ...newWebhookForm, events: [...newWebhookForm.events, evt.id] });
                                    } else {
                                      setNewWebhookForm({ ...newWebhookForm, events: newWebhookForm.events.filter(x => x !== evt.id) });
                                    }
                                  }}
                                  className="rounded border-input text-foreground focus:ring-0"
                                />
                                <span className="text-foreground text-xs">{evt.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* REPORTS & SUMMARIES CATEGORY */}
                        <div className="pt-2 border-t border-border/60">
                          <p className="text-xs font-semibold text-foreground mb-1.5 flex items-center gap-1.5">
                            <FileText className="size-3 text-purple-600" /> Executive Reports &amp; Summaries
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                            {[
                              { id: 'report.daily_summary', label: 'Daily IT Helpdesk Summary' },
                              { id: 'report.support_summary', label: 'Support & SLA Performance' },
                              { id: 'report.assets_summary', label: 'IT Assets Health Report' },
                              { id: 'report.sla_performance', label: 'Weekly SLA Metric Digest' },
                            ].map((evt) => (
                              <label key={evt.id} className="flex items-center gap-2 cursor-pointer text-xs p-1 hover:bg-card rounded">
                                <input
                                  type="checkbox"
                                  checked={newWebhookForm.events.includes(evt.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setNewWebhookForm({ ...newWebhookForm, events: [...newWebhookForm.events, evt.id] });
                                    } else {
                                      setNewWebhookForm({ ...newWebhookForm, events: newWebhookForm.events.filter(x => x !== evt.id) });
                                    }
                                  }}
                                  className="rounded border-input text-foreground focus:ring-0"
                                />
                                <span className="text-foreground text-xs">{evt.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* ASSETS CATEGORY */}
                        <div className="pt-2 border-t border-border/60">
                          <p className="text-xs font-semibold text-foreground mb-1.5 flex items-center gap-1.5">
                            <HardDrive className="size-3 text-emerald-600" /> IT Assets &amp; Hardware
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                            {[
                              { id: 'asset.warranty_expiring', label: 'Warranty Expiring (<= 30d)' },
                              { id: 'asset.maintenance_due', label: 'Maintenance Due' },
                              { id: 'asset.status_changed', label: 'Asset Status Changed' },
                              { id: 'asset.created', label: 'New Asset Provisioned' },
                            ].map((evt) => (
                              <label key={evt.id} className="flex items-center gap-2 cursor-pointer text-xs p-1 hover:bg-card rounded">
                                <input
                                  type="checkbox"
                                  checked={newWebhookForm.events.includes(evt.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setNewWebhookForm({ ...newWebhookForm, events: [...newWebhookForm.events, evt.id] });
                                    } else {
                                      setNewWebhookForm({ ...newWebhookForm, events: newWebhookForm.events.filter(x => x !== evt.id) });
                                    }
                                  }}
                                  className="rounded border-input text-foreground focus:ring-0"
                                />
                                <span className="text-foreground text-xs">{evt.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* INVOICES & FINANCE */}
                        <div className="pt-2 border-t border-border/60">
                          <p className="text-xs font-semibold text-foreground mb-1.5 flex items-center gap-1.5">
                            <Tag className="size-3 text-amber-600" /> Invoices &amp; Billing
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                            {[
                              { id: 'invoice.overdue', label: 'Invoice Overdue Alert' },
                              { id: 'invoice.created', label: 'Invoice Created' },
                              { id: 'invoice.paid', label: 'Invoice Paid' },
                            ].map((evt) => (
                              <label key={evt.id} className="flex items-center gap-2 cursor-pointer text-xs p-1 hover:bg-card rounded">
                                <input
                                  type="checkbox"
                                  checked={newWebhookForm.events.includes(evt.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setNewWebhookForm({ ...newWebhookForm, events: [...newWebhookForm.events, evt.id] });
                                    } else {
                                      setNewWebhookForm({ ...newWebhookForm, events: newWebhookForm.events.filter(x => x !== evt.id) });
                                    }
                                  }}
                                  className="rounded border-input text-foreground focus:ring-0"
                                />
                                <span className="text-foreground text-xs">{evt.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="pt-2 flex items-center justify-between gap-2">
                      <button
                        type="button"
                        onClick={async () => {
                          if (!newWebhookForm.url) {
                            toast.error('Enter a target URL first');
                            return;
                          }
                          await testWebhookEndpoint(newWebhookForm);
                        }}
                        disabled={isTestingWebhook || !newWebhookForm.url}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-border bg-background text-foreground text-xs font-semibold hover:bg-accent disabled:opacity-50"
                      >
                        <Send className="size-3.5" /> Test Webhook Endpoint
                      </button>

                      <button
                        type="submit"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary text-primary-foreground font-semibold text-xs hover:bg-primary/90 transition-all shadow-xs"
                      >
                        <Save className="size-3.5" />
                        {editingWebhook ? 'Update Webhook' : 'Save & Activate Webhook'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              {/* RIGHT COLUMN: SIMULATION TRIGGER + LIVE CARD PREVIEW */}
              <div className="lg:col-span-6 space-y-6">
                {/* ONE-CLICK SIMULATION TRIGGERS */}
                <div className="rounded-xl border border-border bg-card p-5 shadow-xs space-y-4">
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground flex items-center gap-2">
                      <Play className="size-4 text-amber-500" /> One-Click Realtime Simulation Dispatcher
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Trigger instant test events across all active Google Chat, Slack, and generic webhooks to verify connectivity and card rendering.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-2.5">
                    <button
                      type="button"
                      onClick={async () => {
                        await triggerSampleWebhookEvent('ticket.created');
                        setSelectedSampleEvent('ticket.created');
                      }}
                      className="p-3 rounded-lg border border-border bg-sky-50/40 dark:bg-sky-950/20 hover:border-sky-300 text-left transition-all group"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-xs text-sky-700 dark:text-sky-300 flex items-center gap-1.5">
                          <Layers className="size-3.5 text-sky-600" /> New Ticket Created
                        </span>
                        <Send className="size-3 text-sky-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <p className="text-xs text-muted-foreground">Dispatches new ticket notification card</p>
                    </button>

                    <button
                      type="button"
                      onClick={async () => {
                        await triggerSampleWebhookEvent('ticket.sla_breach');
                        setSelectedSampleEvent('ticket.sla_breach');
                      }}
                      className="p-3 rounded-lg border border-border bg-rose-50/40 dark:bg-rose-950/20 hover:border-rose-300 text-left transition-all group"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-xs text-rose-700 dark:text-rose-300 flex items-center gap-1.5">
                          <AlertTriangle className="size-3.5 text-rose-600" /> SLA Breach Alert
                        </span>
                        <Send className="size-3 text-rose-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <p className="text-xs text-muted-foreground">Dispatches critical SLA breach alert card</p>
                    </button>

                    <button
                      type="button"
                      onClick={async () => {
                        await triggerSampleWebhookEvent('report.daily_summary');
                        setSelectedSampleEvent('report.daily_summary');
                      }}
                      className="p-3 rounded-lg border border-border bg-purple-50/40 dark:bg-purple-950/20 hover:border-purple-300 text-left transition-all group"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-xs text-purple-700 dark:text-purple-300 flex items-center gap-1.5">
                          <FileText className="size-3.5 text-purple-600" /> Daily Support Summary
                        </span>
                        <Send className="size-3 text-purple-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <p className="text-xs text-muted-foreground">Dispatches SLA performance executive summary</p>
                    </button>

                    <button
                      type="button"
                      onClick={async () => {
                        await triggerSampleWebhookEvent('asset.warranty_expiring');
                        setSelectedSampleEvent('asset.warranty_expiring');
                      }}
                      className="p-3 rounded-lg border border-border bg-amber-50/40 dark:bg-amber-950/20 hover:border-amber-300 text-left transition-all group"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-xs text-amber-700 dark:text-amber-300 flex items-center gap-1.5">
                          <HardDrive className="size-3.5 text-amber-600" /> Asset Warranty &lt; 30d
                        </span>
                        <Send className="size-3 text-amber-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <p className="text-xs text-muted-foreground">Dispatches hardware warranty expiration</p>
                    </button>
                  </div>
                </div>

                {/* GOOGLE CHAT SPACES & SLACK CARD PREVIEW WIDGET */}
                <div className="rounded-xl border border-border bg-card p-5 shadow-xs space-y-4">
                  <div className="flex items-center justify-between border-b border-border pb-3">
                    <div>
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground flex items-center gap-2">
                        <Bot className="size-4 text-primary" /> Google Workspace Chat Space Card Preview
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Interactive preview of the cardsV2 widget formatted for Google Chat Spaces
                      </p>
                    </div>
                    <select
                      value={selectedSampleEvent}
                      onChange={(e) => setSelectedSampleEvent(e.target.value)}
                      className="h-7 px-2 text-xs font-semibold rounded border border-border bg-background text-foreground outline-none"
                    >
                      <option value="ticket.created">New Ticket Card</option>
                      <option value="ticket.sla_breach">SLA Breach Card</option>
                      <option value="report.daily_summary">Daily SLA Report Card</option>
                      <option value="asset.warranty_expiring">Asset Warranty Card</option>
                      <option value="invoice.overdue">Overdue Invoice Card</option>
                    </select>
                  </div>

                  {/* RENDERED GOOGLE CHAT CARD CONTAINER */}
                  <div className="p-4 bg-muted/60 dark:bg-muted/30 rounded-xl border border-border">
                    <div className="max-w-md mx-auto bg-card rounded-lg border border-border shadow-md overflow-hidden font-sans">
                      {/* CARD HEADER */}
                      <div className="p-3 bg-muted/40 border-b border-border flex items-center gap-3">
                        <div className="size-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-xs shrink-0">
                          {selectedSampleEvent.startsWith('ticket') ? '🎫' : selectedSampleEvent.startsWith('asset') ? '💻' : selectedSampleEvent.startsWith('report') ? '📊' : '💳'}
                        </div>
                        <div className="min-w-0">
                          <h4 className="font-semibold text-xs text-foreground truncate">
                            {selectedSampleEvent === 'ticket.created' && '🎫 New Ticket Created — TB#10492'}
                            {selectedSampleEvent === 'ticket.sla_breach' && '🚨 SLA Target Breached — TB#10492'}
                            {selectedSampleEvent === 'asset.warranty_expiring' && '🛡️ Asset Warranty Expiring Soon (30 Days)'}
                            {selectedSampleEvent === 'report.daily_summary' && '📊 Daily IT Support & SLA Performance Report'}
                            {selectedSampleEvent === 'invoice.overdue' && '⚠️ Overdue Invoice Alert — INV-2026-089'}
                          </h4>
                          <p className="text-xs text-muted-foreground">TeamOne Support Bot • Google Chat Space Notification</p>
                        </div>
                      </div>

                      {/* CARD BODY SECTIONS */}
                      <div className="p-3.5 space-y-3 text-xs">
                        {selectedSampleEvent === 'ticket.created' && (
                          <>
                            <div className="p-2 rounded bg-sky-50 dark:bg-sky-950/40 border border-sky-200 dark:border-sky-800 text-sky-800 dark:text-sky-200 text-xs font-medium">
                              ✨ New customer ticket received and assigned to L2 Engineering Support queue.
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <span className="text-muted-foreground block text-xs">Subject</span>
                                <span className="font-semibold text-foreground">API Latency Spike on US-East</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground block text-xs">Priority / Queue</span>
                                <span className="font-semibold text-rose-600">High • L2 Engineering</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground block text-xs">Requester</span>
                                <span className="font-semibold text-foreground">sarah@enterprise.com</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground block text-xs">Assignee</span>
                                <span className="font-semibold text-foreground">Alex Vance</span>
                              </div>
                            </div>
                          </>
                        )}

                        {selectedSampleEvent === 'ticket.sla_breach' && (
                          <>
                            <div className="p-2 rounded bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-rose-800 dark:text-rose-200 text-xs font-medium">
                              ⚠️ Resolution SLA exceeded target by 1h 45m. Immediate escalation required.
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <span className="text-muted-foreground block text-xs">Ticket Subject</span>
                                <span className="font-semibold text-foreground">Database latency spike &amp; SLA breach</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground block text-xs">Priority / Queue</span>
                                <span className="font-semibold text-rose-600">Critical • L2 Engineering</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground block text-xs">Assignee</span>
                                <span className="font-semibold text-foreground">Alex Vance</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground block text-xs">Requester</span>
                                <span className="font-semibold text-foreground">sarah@enterprise.com</span>
                              </div>
                            </div>
                          </>
                        )}

                        {selectedSampleEvent === 'asset.warranty_expiring' && (
                          <>
                            <div className="p-2 rounded bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 text-xs font-medium">
                              📅 Hardware warranty expires in 27 days. Review renewal or replacement.
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <span className="text-muted-foreground block text-xs">Asset Tag / Serial</span>
                                <span className="font-semibold text-foreground font-mono">AST-9820 (SN: DL99X8)</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground block text-xs">Model &amp; Manufacturer</span>
                                <span className="font-semibold text-foreground">MacBook Pro 16&quot; M3 Max</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground block text-xs">Assigned Custodian</span>
                                <span className="font-semibold text-foreground">Girikumaran (Lead)</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground block text-xs">Warranty Expiry Date</span>
                                <span className="font-semibold text-amber-700 dark:text-amber-400">Oct 15, 2026</span>
                              </div>
                            </div>
                          </>
                        )}

                        {selectedSampleEvent === 'report.daily_summary' && (
                          <>
                            <div className="p-2 rounded bg-purple-50 dark:bg-purple-950/40 border border-purple-200 dark:border-purple-800 text-purple-800 dark:text-purple-200 text-xs font-medium">
                              📈 24h Summary: 48 Resolved • 99.2% SLA Compliance • 0 Open P1 Outages.
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-xs text-center">
                              <div className="p-2 bg-muted/60 rounded border border-border">
                                <span className="text-xs text-muted-foreground block">New Ingested</span>
                                <span className="text-sm font-semibold text-foreground">52</span>
                              </div>
                              <div className="p-2 bg-muted/60 rounded border border-border">
                                <span className="text-xs text-muted-foreground block">Resolved</span>
                                <span className="text-sm font-semibold text-emerald-600">48</span>
                              </div>
                              <div className="p-2 bg-muted/60 rounded border border-border">
                                <span className="text-xs text-muted-foreground block">SLA Compliance</span>
                                <span className="text-sm font-semibold text-primary">99.2%</span>
                              </div>
                            </div>
                          </>
                        )}

                        {selectedSampleEvent === 'invoice.overdue' && (
                          <>
                            <div className="p-2 rounded bg-purple-50 dark:bg-purple-950/40 border border-purple-200 dark:border-purple-800 text-purple-800 dark:text-purple-200 text-xs font-medium">
                              💳 Payment overdue by 14 days. Outstanding balance: $4,850.00 USD.
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <span className="text-muted-foreground block text-xs">Invoice Number</span>
                                <span className="font-semibold text-foreground font-mono">INV-2026-089</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground block text-xs">Client Account</span>
                                <span className="font-semibold text-foreground">Global Enterprise Services</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground block text-xs">Due Date</span>
                                <span className="font-semibold text-purple-700 dark:text-purple-400">Sep 10, 2026</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground block text-xs">Amount Due</span>
                                <span className="font-semibold text-foreground font-mono">$4,850.00</span>
                              </div>
                            </div>
                          </>
                        )}

                        {/* ACTION BUTTONS IN GOOGLE CHAT CARD */}
                        <div className="pt-2 border-t border-border flex items-center justify-end gap-2">
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded bg-muted hover:bg-muted/80 text-foreground border border-border"
                          >
                            <ExternalLink className="size-3" /> View Details
                          </button>
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded bg-foreground text-background hover:bg-foreground/90"
                          >
                            <Check className="size-3" /> Open in TeamOne
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CONFIGURED MULTI-WEBHOOK ENDPOINTS TABLE */}
            <div className="rounded-xl border border-border bg-card p-6 shadow-xs space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-display text-card-title text-foreground flex items-center gap-2">
                    <Radio className="size-4 text-primary" /> Registered Multi-Webhook Integrations ({webhooks.length})
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Active channels receiving real-time event cards and digests
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => fetchWebhooks()}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border bg-background text-xs font-semibold text-foreground hover:bg-accent"
                >
                  <RefreshCw className="size-3.5" /> Refresh List
                </button>
              </div>

              <div className="overflow-x-auto border border-border rounded-lg">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="border-b border-border bg-muted/40 font-semibold text-foreground">
                      <th className="p-3">Platform</th>
                      <th className="p-3">Channel Name &amp; Purpose</th>
                      <th className="p-3">Mode</th>
                      <th className="p-3">Subscribed Events</th>
                      <th className="p-3">Target Endpoint</th>
                      <th className="p-3 text-center">Status</th>
                      <th className="p-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {webhooks.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="p-6 text-center text-muted-foreground">
                          No webhooks registered yet. Pick a preset above or enter a Google Chat Space webhook URL to start.
                        </td>
                      </tr>
                    ) : (
                      webhooks.map((wh) => (
                        <tr key={wh.id} className="hover:bg-muted/20 transition-colors">
                          <td className="p-3 whitespace-nowrap">
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-semibold uppercase bg-primary/10 text-primary border border-primary/20">
                              {wh.platform === 'google_chat' ? 'Google Chat Space' : wh.platform === 'slack' ? 'Slack/Discord' : wh.platform}
                            </span>
                          </td>
                          <td className="p-3">
                            <div className="font-semibold text-foreground">{wh.name}</div>
                            {wh.description && <div className="text-xs text-muted-foreground">{wh.description}</div>}
                          </td>
                          <td className="p-3 whitespace-nowrap">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-emerald-500/10 text-emerald-600 border border-emerald-500/20">
                              Outbound
                            </span>
                            {wh.enableInbound && (
                              <span className="inline-flex items-center gap-1 ml-1 px-1.5 py-0.5 rounded text-xs font-semibold bg-sky-500/10 text-sky-600 border border-sky-500/20">
                                + Inbound
                              </span>
                            )}
                          </td>
                          <td className="p-3">
                            <div className="flex flex-wrap gap-1 max-w-xs">
                              {(wh.events || []).slice(0, 4).map((evt: string) => (
                                <span key={evt} className="px-1.5 py-0.5 rounded bg-muted text-xs font-mono text-foreground border border-border">
                                  {evt.replace('ticket.', '').replace('asset.', '').replace('report.', '').replace('invoice.', '')}
                                </span>
                              ))}
                              {(wh.events || []).length > 4 && (
                                <span className="px-1.5 py-0.5 rounded bg-muted text-xs font-mono text-muted-foreground">
                                  +{(wh.events || []).length - 4} more
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="p-3 font-mono text-xs text-muted-foreground max-w-xs truncate">
                            {wh.url}
                          </td>
                          <td className="p-3 text-center whitespace-nowrap">
                            <button
                              type="button"
                              onClick={async () => {
                                await updateWebhook(wh.id, { isActive: !wh.isActive });
                              }}
                              className={`px-2.5 py-0.5 text-xs font-semibold rounded-full border ${
                                wh.isActive
                                  ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'
                                  : 'bg-muted text-muted-foreground border-border'
                              }`}
                            >
                              {wh.isActive ? 'Active' : 'Disabled'}
                            </button>
                          </td>
                          <td className="p-3 text-right whitespace-nowrap space-x-1.5">
                            <button
                              type="button"
                              onClick={async () => {
                                await testWebhookEndpoint(wh);
                              }}
                              disabled={isTestingWebhook}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded bg-muted hover:bg-accent text-foreground border border-border"
                              title="Send Test Ping"
                            >
                              <Send className="size-3" /> Test Ping
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setEditingWebhook(wh);
                                setNewWebhookForm({
                                  name: wh.name,
                                  url: wh.url,
                                  platform: wh.platform,
                                  direction: wh.direction || 'outbound',
                                  enableInbound: Boolean(wh.enableInbound),
                                  googleSpaceId: wh.googleSpaceId || '',
                                  googleApiKey: wh.googleApiKey || '',
                                  googleApiToken: wh.googleApiToken || '',
                                  events: wh.events || [],
                                  secret: wh.secret || '',
                                  description: wh.description || '',
                                });
                                // Scroll to form
                                window.scrollTo({ top: 300, behavior: 'smooth' });
                              }}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded bg-muted hover:bg-accent text-foreground border border-border"
                            >
                              <Edit className="size-3" /> Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                triggerConfirmation('Delete Webhook', `Are you sure you want to remove webhook "${wh.name}"?`, async () => {
                                  await deleteWebhook(wh.id);
                                });
                              }}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 dark:bg-rose-950/40 dark:border-rose-900"
                            >
                              <Trash2 className="size-3" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* DELIVERY AUDIT LOGS TABLE */}
            <div className="rounded-xl border border-border bg-card p-6 shadow-xs space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-display text-card-title text-foreground flex items-center gap-2">
                    <Terminal className="size-4 text-primary" /> Realtime Webhook Delivery Audit Logs ({webhookLogs.length})
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Live delivery audit trail, HTTP response statuses, and latency metrics
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => fetchWebhookLogs()}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border bg-background text-xs font-semibold text-foreground hover:bg-accent"
                >
                  <RefreshCw className="size-3.5" /> Refresh Logs
                </button>
              </div>

              <div className="overflow-x-auto border border-border rounded-lg max-h-72 overflow-y-auto">
                <table className="w-full text-left text-xs border-collapse font-mono">
                  <thead className="sticky top-0 bg-muted border-b border-border font-sans font-semibold text-foreground">
                    <tr>
                      <th className="p-2.5">Timestamp</th>
                      <th className="p-2.5">Event Type</th>
                      <th className="p-2.5">Destination URL</th>
                      <th className="p-2.5 text-center">HTTP Status</th>
                      <th className="p-2.5 text-right">Response Time</th>
                      <th className="p-2.5 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border text-xs">
                    {webhookLogs.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="p-6 text-center text-muted-foreground font-sans">
                          No webhook delivery logs recorded yet. Send a test ping or trigger an event simulation above.
                        </td>
                      </tr>
                    ) : (
                      webhookLogs.map((log) => (
                        <tr key={log.id} className="hover:bg-muted/30 transition-colors">
                          <td className="p-2.5 text-muted-foreground whitespace-nowrap">
                            {log.deliveredAt ? `${new Date(log.deliveredAt).toLocaleTimeString()} ${new Date(log.deliveredAt).toLocaleDateString()}` : '—'}
                          </td>
                          <td className="p-2.5 whitespace-nowrap">
                            <span className="px-2 py-0.5 rounded bg-primary/10 text-primary border border-primary/20 text-xs">
                              {log.event}
                            </span>
                          </td>
                          <td className="p-2.5 text-muted-foreground max-w-xs truncate font-mono">
                            {log.url}
                          </td>
                          <td className="p-2.5 text-center whitespace-nowrap">
                            {log.statusCode && log.statusCode >= 200 && log.statusCode < 300 ? (
                              <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-600 font-semibold border border-emerald-500/20">
                                {log.statusCode} OK
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 rounded bg-rose-500/10 text-rose-600 font-semibold border border-rose-500/20">
                                {log.statusCode || 'ERR'}
                              </span>
                            )}
                          </td>
                          <td className="p-2.5 text-right text-foreground font-semibold">
                            {log.latencyMs ? `${log.latencyMs}ms` : '—'}
                          </td>
                          <td className="p-2.5 text-center whitespace-nowrap">
                            {log.success ? (
                              <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold">
                                <CheckCircle2 className="size-3" /> Delivered
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-rose-600 font-semibold" title={log.error}>
                                <AlertCircle className="size-3" /> Failed
                              </span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* NOTIFICATIONS & ESCALATION RULES (Moved into Webhooks) */}
        {mainTab === 'webhooks' && webhookSubTab === 'notifications' && (
          <NotificationsView />
        )}
      </div>
    </main>
  </div>

  {/* EDIT QUEUE MAILBOX & DEDICATED CREDENTIALS MODAL */}
      {editingQueueMail && (
        <div className="fixed inset-x-0 bottom-0 top-14 z-50 !m-0 flex justify-end bg-black/50 animate-in fade-in">
          <div className="w-full max-w-xl bg-card border-l border-border h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200 overflow-hidden">
            <div className="flex items-center justify-between border-b border-border p-4 bg-muted/20">
              <div>
                <h3 className="font-display text-card-title text-foreground">Configure Mailbox &amp; Server for Queue "{editingQueueMail.queue_label}"</h3>
                <p className="text-xs text-muted-foreground">Configure dedicated email address and custom mail server credentials.</p>
              </div>
              <button onClick={() => setEditingQueueMail(null)} className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent">
                <X className="size-4" />
              </button>
            </div>

            <form onSubmit={handleUpdateQueueMailAlias} className="flex-1 flex flex-col overflow-hidden text-xs">
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <div>
                <label className="font-semibold text-foreground">Assigned Inbound/Outbound Team Email</label>
                <input
                  type="email"
                  placeholder="e.g. hr@company.com"
                  value={editingQueueMail.emailAddress}
                  onChange={(e) => setEditingQueueMail({ ...editingQueueMail, emailAddress: e.target.value })}
                  className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2.5 font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                />
                <p className="text-xs text-muted-foreground mt-0.5">Leave blank to clear the assigned email address from the team and database.</p>
              </div>

              <div>
                <label className="font-semibold text-foreground block mb-1.5">Mail Server Mode for this Queue</label>
                <div className="space-y-2">
                  <label className="flex items-start gap-2.5 p-2.5 border border-border rounded-lg cursor-pointer hover:bg-accent/30">
                    <input
                      type="radio"
                      name="serverMode"
                      checked={editingQueueMail.serverMode === 'primary_system'}
                      onChange={() => setEditingQueueMail({ ...editingQueueMail, serverMode: 'primary_system' })}
                      className="mt-0.5"
                    />
                    <div>
                      <span className="font-semibold text-foreground block">Inherit System Primary Mail Server</span>
                      <span className="text-xs text-muted-foreground">Use the global Google Workspace / SMTP server settings for processing queue emails.</span>
                    </div>
                  </label>

                  <label className="flex items-start gap-2.5 p-2.5 border border-border rounded-lg cursor-pointer hover:bg-accent/30">
                    <input
                      type="radio"
                      name="serverMode"
                      checked={editingQueueMail.serverMode === 'dedicated_smtp'}
                      onChange={() => setEditingQueueMail({ ...editingQueueMail, serverMode: 'dedicated_smtp' })}
                      className="mt-0.5"
                    />
                    <div>
                      <span className="font-semibold text-foreground block">Dedicated Custom SMTP &amp; IMAP Server for this Queue</span>
                      <span className="text-xs text-muted-foreground">Outbound SMTP to send agent replies FROM queue email. Inbound IMAP to fetch new emails &amp; thread replies into existing tickets.</span>
                    </div>
                  </label>

                  <label className="flex items-start gap-2.5 p-2.5 border border-border rounded-lg cursor-pointer hover:bg-accent/30">
                    <input
                      type="radio"
                      name="serverMode"
                      checked={editingQueueMail.serverMode === 'dedicated_google'}
                      onChange={() => setEditingQueueMail({ ...editingQueueMail, serverMode: 'dedicated_google' })}
                      className="mt-0.5"
                    />
                    <div>
                      <span className="font-semibold text-foreground block">Dedicated Google Workspace Account for this Queue</span>
                      <span className="text-xs text-muted-foreground">Connect a dedicated Google Workspace OAuth2 account for this queue.</span>
                    </div>
                  </label>
                </div>
              </div>

              {/* THREAD MATCHING TOGGLE */}
              <div className="p-2.5 bg-muted/30 border border-border rounded-lg">
                <label className="flex items-center gap-2.5 cursor-pointer text-xs">
                  <input
                    type="checkbox"
                    checked={editingQueueMail.threadMatchingEnabled ?? true}
                    onChange={(e) => setEditingQueueMail({ ...editingQueueMail, threadMatchingEnabled: e.target.checked })}
                    className="rounded border-input text-foreground"
                  />
                  <div>
                    <span className="font-semibold text-foreground block">Enable Email Thread Matching</span>
                    <span className="text-xs text-muted-foreground">When a client replies to an existing ticket email thread, the reply is matched and appended to the same ticket instead of creating a new one. Matches by Message-ID, In-Reply-To, and References headers.</span>
                  </div>
                </label>
              </div>

              {/* DEDICATED CUSTOM SMTP + IMAP SERVER FIELDS */}
              {editingQueueMail.serverMode === 'dedicated_smtp' && (
                <div className="space-y-4">
                  {/* MAIL FLOW EXPLANATION */}
                  <div className="p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-500/30 rounded-lg text-xs text-blue-900 dark:text-blue-200 space-y-1">
                    <p className="font-semibold">📧 How the Dedicated Mail Pipeline Works:</p>
                    <p>• <strong>Inbound (IMAP):</strong> System polls the queue IMAP mailbox → New email creates a ticket. Reply on same thread updates existing ticket.</p>
                    <p>• <strong>Outbound (SMTP):</strong> Agent replies from ticket → Reply is sent FROM <code className="px-1 py-0.5 bg-blue-100 dark:bg-blue-900 rounded font-mono">{editingQueueMail.emailAddress}</code> via the dedicated SMTP server.</p>
                  </div>

                  {/* OUTBOUND SMTP */}
                  <div className="p-3 bg-muted/40 border border-border rounded-lg space-y-3">
                    <h4 className="font-semibold text-xs text-foreground uppercase tracking-wider flex items-center gap-1.5">
                      <ArrowRight className="size-3.5 text-blue-600" /> Outbound SMTP Server (Agent Replies &amp; Notifications)
                    </h4>
                    <p className="text-xs text-muted-foreground -mt-1">Agent replies on tickets in this queue will be sent FROM <strong className="text-foreground">{editingQueueMail.emailAddress}</strong></p>
                    <div className="grid grid-cols-3 gap-2.5">
                      <div>
                        <label className="font-semibold text-muted-foreground">SMTP Host *</label>
                        <input
                          type="text"
                          placeholder="smtp.company.com"
                          value={editingQueueMail.dedicatedSmtpHost}
                          onChange={(e) => setEditingQueueMail({ ...editingQueueMail, dedicatedSmtpHost: e.target.value })}
                          className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2.5 font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                        />
                      </div>
                      <div>
                        <label className="font-semibold text-muted-foreground">Port *</label>
                        <input
                          type="text"
                          placeholder="587"
                          value={editingQueueMail.dedicatedSmtpPort}
                          onChange={(e) => setEditingQueueMail({ ...editingQueueMail, dedicatedSmtpPort: e.target.value })}
                          className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2.5 font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                        />
                      </div>
                      <div>
                        <label className="font-semibold text-muted-foreground">Security</label>
                        <select
                          value={editingQueueMail.dedicatedSmtpSecurity || 'TLS'}
                          onChange={(e) => setEditingQueueMail({ ...editingQueueMail, dedicatedSmtpSecurity: e.target.value })}
                          className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                        >
                          <option value="TLS">STARTTLS</option>
                          <option value="SSL">SSL / TLS</option>
                          <option value="None">None</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2.5">
                      <div>
                        <label className="font-semibold text-muted-foreground">Username *</label>
                        <input
                          type="text"
                          placeholder={editingQueueMail.emailAddress || 'user@company.com'}
                          value={editingQueueMail.dedicatedSmtpUser}
                          onChange={(e) => setEditingQueueMail({ ...editingQueueMail, dedicatedSmtpUser: e.target.value })}
                          className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2.5 font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                        />
                      </div>
                      <div>
                        <label className="font-semibold text-muted-foreground">Password *</label>
                        <input
                          type="password"
                          placeholder="••••••••••••"
                          value={editingQueueMail.dedicatedSmtpPass}
                          onChange={(e) => setEditingQueueMail({ ...editingQueueMail, dedicatedSmtpPass: e.target.value })}
                          className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2.5 font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                        />
                      </div>
                    </div>
                  </div>

                  {/* INBOUND IMAP */}
                  <div className="p-3 bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-500/30 rounded-lg space-y-3">
                    <h4 className="font-semibold text-xs text-emerald-800 dark:text-emerald-300 uppercase tracking-wider flex items-center gap-1.5">
                      <Mail className="size-3.5 text-emerald-600" /> Inbound IMAP Server (Fetch Emails → Create/Update Tickets)
                    </h4>
                    <p className="text-xs text-muted-foreground -mt-1">Polls inbox of <strong className="text-foreground">{editingQueueMail.emailAddress}</strong> to auto-create new tickets and match thread replies to existing tickets.</p>
                    <div className="grid grid-cols-3 gap-2.5">
                      <div>
                        <label className="font-semibold text-muted-foreground">IMAP Host *</label>
                        <input
                          type="text"
                          placeholder="imap.company.com"
                          value={editingQueueMail.dedicatedImapHost}
                          onChange={(e) => setEditingQueueMail({ ...editingQueueMail, dedicatedImapHost: e.target.value })}
                          className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2.5 font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                        />
                      </div>
                      <div>
                        <label className="font-semibold text-muted-foreground">Port *</label>
                        <input
                          type="text"
                          placeholder="993"
                          value={editingQueueMail.dedicatedImapPort}
                          onChange={(e) => setEditingQueueMail({ ...editingQueueMail, dedicatedImapPort: e.target.value })}
                          className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2.5 font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                        />
                      </div>
                      <div>
                        <label className="font-semibold text-muted-foreground">Security</label>
                        <select
                          value={editingQueueMail.dedicatedImapSecurity || 'SSL'}
                          onChange={(e) => setEditingQueueMail({ ...editingQueueMail, dedicatedImapSecurity: e.target.value })}
                          className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                        >
                          <option value="SSL">SSL / TLS</option>
                          <option value="TLS">STARTTLS</option>
                          <option value="None">None</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2.5">
                      <div>
                        <label className="font-semibold text-muted-foreground">Username *</label>
                        <input
                          type="text"
                          placeholder={editingQueueMail.emailAddress || 'user@company.com'}
                          value={editingQueueMail.dedicatedImapUser}
                          onChange={(e) => setEditingQueueMail({ ...editingQueueMail, dedicatedImapUser: e.target.value })}
                          className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2.5 font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                        />
                      </div>
                      <div>
                        <label className="font-semibold text-muted-foreground">Password *</label>
                        <input
                          type="password"
                          placeholder="••••••••••••"
                          value={editingQueueMail.dedicatedImapPass}
                          onChange={(e) => setEditingQueueMail({ ...editingQueueMail, dedicatedImapPass: e.target.value })}
                          className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2.5 font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* DEDICATED GOOGLE OAUTH FIELDS */}
              {editingQueueMail.serverMode === 'dedicated_google' && (
                <div className="p-3 bg-blue-50/50 dark:bg-blue-950/30 border border-blue-500/30 rounded-lg space-y-3 pt-2 text-xs">
                  <h4 className="font-semibold text-xs text-foreground uppercase tracking-wider text-blue-900 dark:text-blue-200">Dedicated Google OAuth Credentials</h4>
                  <div>
                    <label className="font-semibold text-muted-foreground">Google OAuth Client ID</label>
                    <input
                      type="text"
                      placeholder="hr-queue-oauth.googleusercontent.com"
                      className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2.5 font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                    />
                  </div>
                </div>
              )}

              </div>

              <div className="border-t border-border p-4 bg-card flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setEditingQueueMail(null)}
                  className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-accent"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-md bg-primary text-primary-foreground px-4 py-1.5 text-xs font-semibold hover:bg-primary/90 flex items-center gap-1.5 cursor-pointer shadow-xs"
                >
                  <Check className="size-3.5" /> Apply &amp; Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DOUBLE-CONFIRMATION DRAWER */}
      {confirmModal.isOpen && (
        <div className="fixed inset-x-0 bottom-0 top-14 z-50 !m-0 flex justify-end bg-black/50 animate-in fade-in">
          <div className="w-full max-w-md bg-card border-l border-border h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200 overflow-hidden">
            <div className="p-4 border-b border-border flex items-center justify-between bg-destructive/10">
              <div className="flex items-center gap-2.5 text-destructive">
                <AlertTriangle className="size-5 shrink-0" />
                <h3 className="font-display text-card-title text-foreground">{confirmModal.title}</h3>
              </div>
              <button
                onClick={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} })}
                className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent"
              >
                <X className="size-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4 text-xs">
              <p className="text-muted-foreground leading-relaxed">{confirmModal.message}</p>
            </div>
            <div className="border-t border-border p-4 bg-card flex justify-end gap-2">
              <button
                onClick={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} })}
                className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-accent"
              >
                Cancel
              </button>
              <button
                onClick={confirmModal.onConfirm}
                className="rounded-md bg-destructive px-4 py-1.5 text-xs font-semibold text-white hover:bg-destructive/90 shadow"
              >
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TEST DIAGNOSTIC EMAIL DRAWER */}
      {showTestEmailModal && (
        <div className="fixed inset-x-0 bottom-0 top-14 z-50 !m-0 flex justify-end bg-black/50 animate-in fade-in">
          <div className="w-full max-w-lg bg-card border-l border-border h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200 overflow-hidden">
            <div className="p-4 border-b border-border flex items-center justify-between bg-muted/20">
              <h3 className="font-display text-card-title text-foreground">Diagnostic Mail Connection Test</h3>
              <button onClick={() => setShowTestEmailModal(false)} className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent">
                <X className="size-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4 text-xs">
              <div>
                <label className="font-semibold text-foreground">Test Recipient Email Address</label>
                <input
                  type="email"
                  value={testEmailRecipient}
                  onChange={(e) => setTestEmailRecipient(e.target.value)}
                  className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs font-mono text-foreground outline-none focus:ring-1 focus:ring-ring"
                />
              </div>

              {testEmailLogs.length > 0 && (
                <div className="p-3 bg-black text-emerald-400 font-mono text-xs rounded-lg space-y-1 max-h-60 overflow-y-auto border border-border">
                  {testEmailLogs.map((log, i) => (
                    <p key={i}>{log}</p>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-border p-4 bg-card flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowTestEmailModal(false)}
                className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-accent"
              >
                Close
              </button>
              <button
                type="button"
                onClick={handleRunTestOutboundEmail}
                disabled={isTestingEmail}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 shadow-xs cursor-pointer"
              >
                <RefreshCw className={`size-3.5 ${isTestingEmail ? 'animate-spin' : ''}`} />
                {isTestingEmail ? 'Testing...' : 'Send Outbound Test Email'}
              </button>
            </div>
          </div>
        </div>
      )}



      {/* DETAILED ADD NEW SUPPORT TEAM DRAWER */}
      {showAddTeamModal && (
        <div className="fixed inset-x-0 bottom-0 top-14 z-50 !m-0 flex justify-end bg-black/50 animate-in fade-in">
          <div className="w-full max-w-2xl bg-card border-l border-border h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200">
            <div className="flex items-center justify-between border-b border-border p-4 bg-muted/20">
              <div className="flex items-center gap-2.5">
                <div className="flex size-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Users className="size-4" />
                </div>
                <div>
                  <h3 className="font-display text-card-title text-foreground">Create New Support Team</h3>
                  <p className="text-xs text-muted-foreground">Configure departmental queue routing, email mailbox, SLA target, and agent members.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowAddTeamModal(false)}
                className="text-muted-foreground hover:text-foreground cursor-pointer p-1 rounded-md"
              >
                <X className="size-4" />
              </button>
            </div>

            <form onSubmit={handleCreateTeamDetailed} className="p-5 overflow-y-auto space-y-4 text-xs">
              {/* Section 1: Team Identity */}
              <div className="p-3.5 rounded-xl border border-border bg-muted/20 space-y-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <Tag className="size-3.5 text-primary" /> Team Identity &amp; Routing Key
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="font-semibold text-foreground block mb-1">Team Display Name / Label *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. L3 Engineering Escalations"
                      value={newTeamForm.label}
                      onChange={(e) => {
                        const val = e.target.value;
                        const autoSlug = val.toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '');
                        setNewTeamForm((prev) => ({
                          ...prev,
                          label: val,
                          key: prev.key === '' || prev.key === autoSlug.slice(0, -1) ? autoSlug : prev.key,
                        }));
                      }}
                      className="h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                    />
                  </div>
                  <div>
                    <label className="font-semibold text-foreground block mb-1">
                      Routing Key / Identifier * <span className="text-muted-foreground font-normal">(URL &amp; API slug)</span>
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. l3-engineering"
                      value={newTeamForm.key}
                      onChange={(e) => setNewTeamForm({ ...newTeamForm, key: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                      className="h-8 w-full rounded-md border border-input bg-background px-2.5 font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                    />
                  </div>
                </div>
                <div>
                  <label className="font-semibold text-foreground block mb-1">Team Description &amp; Scope</label>
                  <textarea
                    rows={2}
                    placeholder="Describe this team's specialization, scope of work, and escalation responsibilities..."
                    value={newTeamForm.description}
                    onChange={(e) => setNewTeamForm({ ...newTeamForm, description: e.target.value })}
                    className="w-full rounded-md border border-input bg-background p-2 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring resize-none"
                  />
                </div>
              </div>

              {/* Section 2: Contact & SLA */}
              <div className="p-3.5 rounded-xl border border-border bg-muted/20 space-y-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <Mail className="size-3.5 text-primary" /> Communication &amp; SLA Target
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="font-semibold text-foreground block mb-1">Dedicated Team Email Address</label>
                    <input
                      type="email"
                      placeholder="e.g. l3-support@company.com"
                      value={newTeamForm.email}
                      onChange={(e) => setNewTeamForm({ ...newTeamForm, email: e.target.value })}
                      className="h-8 w-full rounded-md border border-input bg-background px-2.5 font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                    />
                  </div>
                  <div>
                    <label className="font-semibold text-foreground block mb-1">Default SLA Policy</label>
                    <select
                      value={newTeamForm.defaultSla}
                      onChange={(e) => setNewTeamForm({ ...newTeamForm, defaultSla: e.target.value })}
                      className="h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring cursor-pointer"
                    >
                      {availableSlaPolicies.map((pol: string) => (
                        <option key={pol} value={pol}>{pol}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer pt-1">
                  <input
                    type="checkbox"
                    checked={newTeamForm.threadMatchingEnabled}
                    onChange={(e) => setNewTeamForm({ ...newTeamForm, threadMatchingEnabled: e.target.checked })}
                    className="rounded border-input text-foreground focus:ring-0 cursor-pointer"
                  />
                  <span className="text-xs font-semibold text-foreground">
                    Enable intelligent conversation thread matching &amp; ticket auto-reopen
                  </span>
                </label>
              </div>

              {/* Section 3: Mail Server Infrastructure */}
              <div className="p-3.5 rounded-xl border border-border bg-muted/20 space-y-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <Server className="size-3.5 text-primary" /> Mail Server Infrastructure Mode
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {[
                    { id: 'primary_system', title: 'System Default', desc: 'Inherit global mail server' },
                    { id: 'dedicated_smtp', title: 'Dedicated SMTP/IMAP', desc: 'Custom mail server credentials' },
                    { id: 'dedicated_google', title: 'Google Workspace', desc: 'OAuth & Workspace API' },
                  ].map((mode) => (
                    <button
                      key={mode.id}
                      type="button"
                      onClick={() => setNewTeamForm({ ...newTeamForm, serverMode: mode.id as any })}
                      className={cn(
                        "p-2.5 rounded-lg border text-left transition-all cursor-pointer",
                        newTeamForm.serverMode === mode.id
                          ? "border-primary bg-primary/5 ring-1 ring-primary"
                          : "border-border bg-card hover:bg-muted/40"
                      )}
                    >
                      <span className={cn("text-xs font-semibold block", newTeamForm.serverMode === mode.id ? "text-foreground" : "text-muted-foreground")}>
                        {mode.title}
                      </span>
                      <span className="text-xs text-muted-foreground mt-0.5 block">{mode.desc}</span>
                    </button>
                  ))}
                </div>

                {newTeamForm.serverMode === 'dedicated_smtp' && (
                  <div className="pt-2 border-t border-border space-y-3">
                    <p className="text-xs text-muted-foreground">Configure custom SMTP &amp; IMAP servers for this specific support team mailbox:</p>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-semibold text-muted-foreground mb-0.5">SMTP Host</label>
                        <input
                          type="text"
                          placeholder="smtp.company.com"
                          value={newTeamForm.dedicatedSmtpHost}
                          onChange={(e) => setNewTeamForm({ ...newTeamForm, dedicatedSmtpHost: e.target.value })}
                          className="h-7 w-full rounded border border-input bg-background px-2 font-mono text-xs text-foreground outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-muted-foreground mb-0.5">SMTP Port</label>
                        <input
                          type="text"
                          placeholder="587"
                          value={newTeamForm.dedicatedSmtpPort}
                          onChange={(e) => setNewTeamForm({ ...newTeamForm, dedicatedSmtpPort: e.target.value })}
                          className="h-7 w-full rounded border border-input bg-background px-2 font-mono text-xs text-foreground outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-muted-foreground mb-0.5">Security</label>
                        <select
                          value={newTeamForm.dedicatedSmtpSecurity}
                          onChange={(e) => setNewTeamForm({ ...newTeamForm, dedicatedSmtpSecurity: e.target.value })}
                          className="h-7 w-full rounded border border-input bg-background px-1.5 text-xs text-foreground outline-none"
                        >
                          <option value="TLS">TLS</option>
                          <option value="SSL">SSL</option>
                          <option value="STARTTLS">STARTTLS</option>
                          <option value="None">None</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-semibold text-muted-foreground mb-0.5">SMTP Username</label>
                        <input
                          type="text"
                          placeholder="user@company.com"
                          value={newTeamForm.dedicatedSmtpUser}
                          onChange={(e) => setNewTeamForm({ ...newTeamForm, dedicatedSmtpUser: e.target.value })}
                          className="h-7 w-full rounded border border-input bg-background px-2 font-mono text-xs text-foreground outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-muted-foreground mb-0.5">SMTP Password</label>
                        <input
                          type="password"
                          placeholder="••••••••••••"
                          value={newTeamForm.dedicatedSmtpPass}
                          onChange={(e) => setNewTeamForm({ ...newTeamForm, dedicatedSmtpPass: e.target.value })}
                          className="h-7 w-full rounded border border-input bg-background px-2 font-mono text-xs text-foreground outline-none"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 pt-1 border-t border-border/60">
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-semibold text-muted-foreground mb-0.5">IMAP Host</label>
                        <input
                          type="text"
                          placeholder="imap.company.com"
                          value={newTeamForm.dedicatedImapHost}
                          onChange={(e) => setNewTeamForm({ ...newTeamForm, dedicatedImapHost: e.target.value })}
                          className="h-7 w-full rounded border border-input bg-background px-2 font-mono text-xs text-foreground outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-muted-foreground mb-0.5">IMAP Port</label>
                        <input
                          type="text"
                          placeholder="993"
                          value={newTeamForm.dedicatedImapPort}
                          onChange={(e) => setNewTeamForm({ ...newTeamForm, dedicatedImapPort: e.target.value })}
                          className="h-7 w-full rounded border border-input bg-background px-2 font-mono text-xs text-foreground outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-muted-foreground mb-0.5">Security</label>
                        <select
                          value={newTeamForm.dedicatedImapSecurity}
                          onChange={(e) => setNewTeamForm({ ...newTeamForm, dedicatedImapSecurity: e.target.value })}
                          className="h-7 w-full rounded border border-input bg-background px-1.5 text-xs text-foreground outline-none"
                        >
                          <option value="SSL">SSL</option>
                          <option value="TLS">TLS</option>
                          <option value="STARTTLS">STARTTLS</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Section 4: Team Member Assignments */}
              <div className="p-3.5 rounded-xl border border-border bg-muted/20 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                    <UserCheck className="size-3.5 text-primary" /> Assign Team Members ({newTeamForm.assignedAgentIds.length} of {agents.length})
                  </span>
                  <span className="text-xs text-muted-foreground">Select staff assigned to this team</span>
                </div>

                {agents.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-2 italic">No agents registered in staff directory yet.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-36 overflow-y-auto p-1">
                    {agents.map((ag) => {
                      const isSelected = newTeamForm.assignedAgentIds.includes(String(ag.id));
                      return (
                        <label
                          key={ag.id}
                          className={cn(
                            "flex items-center gap-2.5 p-2 rounded-lg border text-xs cursor-pointer transition-colors",
                            isSelected ? "border-primary/50 bg-primary/5" : "border-border bg-card hover:bg-accent/40"
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                              const strId = String(ag.id);
                              setNewTeamForm((prev) => ({
                                ...prev,
                                assignedAgentIds: isSelected
                                  ? prev.assignedAgentIds.filter((id) => id !== strId)
                                  : [...prev.assignedAgentIds, strId],
                              }));
                            }}
                            className="rounded border-input text-foreground focus:ring-0 cursor-pointer"
                          />
                          <div className="flex-1 min-w-0">
                            <span className="font-semibold text-foreground truncate block">
                              {ag.first_name} {ag.last_name}
                            </span>
                            <span className="text-xs text-muted-foreground truncate block font-mono">
                              {ag.email || ag.role}
                            </span>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Form Footer */}
              <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
                <button
                  type="button"
                  onClick={() => setShowAddTeamModal(false)}
                  className="rounded-lg border border-border px-3.5 py-1.5 text-xs font-semibold text-foreground hover:bg-accent cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-primary text-primary-foreground px-4 py-1.5 text-xs font-semibold shadow-xs hover:bg-primary/90 transition-all cursor-pointer"
                >
                  Create Team
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DETAILED EDIT SUPPORT TEAM DRAWER */}
      {editingQueue && (
        <div className="fixed inset-x-0 bottom-0 top-14 z-50 !m-0 flex justify-end bg-black/50 animate-in fade-in">
          <div className="flex h-full w-full max-w-lg flex-col border-l border-border bg-card shadow-lg animate-in slide-in-from-right duration-200">
            <div className="flex items-center justify-between border-b border-border p-4 bg-muted/20">
              <div className="flex items-center gap-2.5">
                <div className="flex size-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Layers className="size-4" />
                </div>
                <div>
                  <h3 className="font-display text-card-title text-foreground">
                    {editingQueue.queue_label || editingQueue.label || editingQueue.team_name}
                  </h3>
                  <p className="text-xs text-muted-foreground">{editingQueue.emailAddress || editingQueue.email_address || editingQueue.queue_key || editingQueue.team_key || 'Support team'}</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {!isTeamEditMode ? (
                  <button
                    type="button"
                    onClick={() => setIsTeamEditMode(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-card text-foreground hover:bg-accent text-xs font-semibold transition-all cursor-pointer shadow-2xs"
                  >
                    <Lock className="size-3.5 text-muted-foreground" />
                    <span>Edit settings</span>
                  </button>
                ) : (
                  <>
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400 text-xs font-semibold">
                      <Unlock className="size-3" />
                      <span>Editing</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => setIsTeamEditMode(false)}
                      className="px-2.5 py-1 text-xs text-muted-foreground hover:text-foreground cursor-pointer"
                    >
                      Discard
                    </button>
                  </>
                )}

                <button
                  type="button"
                  onClick={() => { setEditingQueue(null); setIsTeamEditMode(false); }}
                  className="text-muted-foreground hover:text-foreground cursor-pointer p-1 rounded-md"
                >
                  <X className="size-4" />
                </button>
              </div>
            </div>

            {/* Edit Mode Status Banner */}
            {!isTeamEditMode ? (
              <div className="mx-5 mt-4 p-2.5 rounded-lg bg-muted/40 border border-border text-xs text-muted-foreground flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Lock className="size-3.5 text-muted-foreground shrink-0" />
                  <span>Viewing Mode (Read-Only). Click <strong>"Enable Edit Mode"</strong> to modify team details, email, SLA targets, and mail credentials.</span>
                </div>
                <button
                  type="button"
                  onClick={() => setIsTeamEditMode(true)}
                  className="text-primary font-semibold hover:underline cursor-pointer text-xs"
                >
                  Enable Edit Mode
                </button>
              </div>
            ) : (
              <div className="mx-5 mt-4 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs text-amber-900 dark:text-amber-200 flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Sparkles className="size-3.5 text-amber-600 shrink-0" />
                  <span><strong>Edit Mode is ON:</strong> Fields are unlocked. Click <strong>"Apply &amp; Save"</strong> to commit changes to the database.</span>
                </span>
              </div>
            )}

            <form onSubmit={handleUpdateQueue} className="p-5 overflow-y-auto space-y-4 text-xs">
              {/* Section 1: Team Identity */}
              <div className="p-3.5 rounded-xl border border-border bg-muted/20 space-y-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <Tag className="size-3.5 text-primary" /> Team Identity &amp; Routing Key
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="font-semibold text-foreground block mb-1">Team Display Name / Label *</label>
                    <input
                      type="text"
                      required
                      disabled={!isTeamEditMode}
                      value={editingQueue.queue_label || editingQueue.label || ''}
                      onChange={(e) => setEditingQueue({ ...editingQueue, queue_label: e.target.value, label: e.target.value, team_name: e.target.value })}
                      className="h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="font-semibold text-foreground block mb-1">
                      Routing Key / Identifier <span className="text-muted-foreground font-normal">(System slug)</span>
                    </label>
                    <input
                      type="text"
                      readOnly
                      value={editingQueue.queue_key || editingQueue.key || editingQueue.team_key || ''}
                      className="h-8 w-full rounded-md border border-input bg-muted/60 px-2.5 font-mono text-xs text-muted-foreground outline-none cursor-not-allowed"
                    />
                  </div>
                </div>
                <div>
                  <label className="font-semibold text-foreground block mb-1">Team Description &amp; Scope</label>
                  <textarea
                    rows={2}
                    disabled={!isTeamEditMode}
                    placeholder="Describe this team's specialization, scope of work, and escalation responsibilities..."
                    value={editingQueue.description || ''}
                    onChange={(e) => setEditingQueue({ ...editingQueue, description: e.target.value })}
                    className="w-full rounded-md border border-input bg-background p-2 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring resize-none disabled:opacity-60 disabled:cursor-not-allowed"
                  />
                </div>
              </div>

              {/* Section 2: Contact & SLA */}
              <div className="p-3.5 rounded-xl border border-border bg-muted/20 space-y-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <Mail className="size-3.5 text-primary" /> Communication &amp; SLA Target
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="font-semibold text-foreground block mb-1">Assigned Inbound/Outbound Team Email</label>
                    <input
                      type="email"
                      placeholder="e.g. hr@company.com"
                      disabled={!isTeamEditMode}
                      value={editingQueue.configuredEmail !== undefined ? editingQueue.configuredEmail : (editingQueue.emailAddress || '')}
                      onChange={(e) => setEditingQueue({
                        ...editingQueue,
                        configuredEmail: e.target.value,
                        emailAddress: e.target.value,
                        email_address: e.target.value,
                        email_routing: e.target.value,
                      })}
                      className="h-8 w-full rounded-md border border-input bg-background px-2.5 font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {((editingQueue.configuredEmail || editingQueue.emailAddress || '').trim()) ? (
                        <>Configured team email: <span className="font-mono text-foreground font-semibold">{(editingQueue.configuredEmail || editingQueue.emailAddress).trim()}</span>. Leave blank to clear the assigned email address from the team and database.</>
                      ) : (
                        <>Leave blank to clear the assigned email address from the team and database.</>
                      )}
                    </p>
                  </div>
                  <div>
                    <label className="font-semibold text-foreground block mb-1">Default SLA Policy Target</label>
                    <select
                      disabled={!isTeamEditMode}
                      value={editingQueue.default_sla || editingQueue.defaultSla || 'Default SLA Policy'}
                      onChange={(e) => setEditingQueue({ ...editingQueue, default_sla: e.target.value, defaultSla: e.target.value })}
                      className="h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {availableSlaPolicies.map((pol: string) => (
                        <option key={pol} value={pol}>{pol}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <label className={cn("flex items-center gap-2 pt-1", isTeamEditMode ? "cursor-pointer" : "cursor-default opacity-80")}>
                  <input
                    type="checkbox"
                    disabled={!isTeamEditMode}
                    checked={editingQueue.threadMatchingEnabled ?? editingQueue.thread_matching_enabled ?? true}
                    onChange={(e) => setEditingQueue({
                      ...editingQueue,
                      threadMatchingEnabled: e.target.checked,
                      thread_matching_enabled: e.target.checked,
                    })}
                    className="rounded border-input text-foreground focus:ring-0 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  />
                  <span className="text-xs font-semibold text-foreground">
                    Enable intelligent conversation thread matching &amp; ticket auto-reopen
                  </span>
                </label>
              </div>

              {/* Section 3: Mail Server Infrastructure */}
              <div className="p-3.5 rounded-xl border border-border bg-muted/20 space-y-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <Server className="size-3.5 text-primary" /> Mail Server Infrastructure Mode
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {[
                    { id: 'primary_system', title: 'System Default', desc: 'Inherit global mail server' },
                    { id: 'dedicated_smtp', title: 'Dedicated SMTP/IMAP', desc: 'Custom mail server credentials' },
                    { id: 'dedicated_google', title: 'Google Workspace', desc: 'OAuth & Workspace API' },
                  ].map((mode) => {
                    const currentMode = editingQueue.serverMode || editingQueue.server_mode || 'primary_system';
                    const isSelected = currentMode === mode.id;
                    return (
                      <button
                        key={mode.id}
                        type="button"
                        disabled={!isTeamEditMode}
                        onClick={() => isTeamEditMode && setEditingQueue({ ...editingQueue, serverMode: mode.id, server_mode: mode.id })}
                        className={cn(
                          "p-2.5 rounded-lg border text-left transition-all",
                          isTeamEditMode ? "cursor-pointer" : "cursor-default",
                          isSelected
                            ? "border-primary bg-primary/5 ring-1 ring-primary"
                            : "border-border bg-card hover:bg-muted/40",
                          !isTeamEditMode && "opacity-80"
                        )}
                      >
                        <span className={cn("text-xs font-semibold block", isSelected ? "text-foreground" : "text-muted-foreground")}>
                          {mode.title}
                        </span>
                        <span className="text-xs text-muted-foreground mt-0.5 block">{mode.desc}</span>
                      </button>
                    );
                  })}
                </div>

                {(editingQueue.serverMode || editingQueue.server_mode) === 'dedicated_smtp' && (
                  <div className="pt-2 border-t border-border space-y-3">
                    <p className="text-xs text-muted-foreground">Configure custom SMTP &amp; IMAP servers for this specific support team mailbox:</p>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-semibold text-muted-foreground mb-0.5">SMTP Host</label>
                        <input
                          type="text"
                          disabled={!isTeamEditMode}
                          placeholder="smtp.company.com"
                          value={editingQueue.dedicatedSmtpHost || editingQueue.dedicated_smtp_host || ''}
                          onChange={(e) => setEditingQueue({ ...editingQueue, dedicatedSmtpHost: e.target.value, dedicated_smtp_host: e.target.value })}
                          className="h-7 w-full rounded border border-input bg-background px-2 font-mono text-xs text-foreground outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-muted-foreground mb-0.5">SMTP Port</label>
                        <input
                          type="text"
                          disabled={!isTeamEditMode}
                          placeholder="587"
                          value={editingQueue.dedicatedSmtpPort || editingQueue.dedicated_smtp_port || '587'}
                          onChange={(e) => setEditingQueue({ ...editingQueue, dedicatedSmtpPort: e.target.value, dedicated_smtp_port: e.target.value })}
                          className="h-7 w-full rounded border border-input bg-background px-2 font-mono text-xs text-foreground outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-muted-foreground mb-0.5">Security</label>
                        <select
                          disabled={!isTeamEditMode}
                          value={editingQueue.dedicatedSmtpSecurity || editingQueue.dedicated_smtp_security || 'TLS'}
                          onChange={(e) => setEditingQueue({ ...editingQueue, dedicatedSmtpSecurity: e.target.value, dedicated_smtp_security: e.target.value })}
                          className="h-7 w-full rounded border border-input bg-background px-1.5 text-xs text-foreground outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          <option value="TLS">TLS</option>
                          <option value="SSL">SSL</option>
                          <option value="STARTTLS">STARTTLS</option>
                          <option value="None">None</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-semibold text-muted-foreground mb-0.5">SMTP Username</label>
                        <input
                          type="text"
                          disabled={!isTeamEditMode}
                          placeholder="user@company.com"
                          value={editingQueue.dedicatedSmtpUser || editingQueue.dedicated_smtp_user || ''}
                          onChange={(e) => setEditingQueue({ ...editingQueue, dedicatedSmtpUser: e.target.value, dedicated_smtp_user: e.target.value })}
                          className="h-7 w-full rounded border border-input bg-background px-2 font-mono text-xs text-foreground outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-muted-foreground mb-0.5">SMTP Password</label>
                        <input
                          type="password"
                          disabled={!isTeamEditMode}
                          placeholder="••••••••••••"
                          value={editingQueue.dedicatedSmtpPass || editingQueue.dedicated_smtp_pass || ''}
                          onChange={(e) => setEditingQueue({ ...editingQueue, dedicatedSmtpPass: e.target.value, dedicated_smtp_pass: e.target.value })}
                          className="h-7 w-full rounded border border-input bg-background px-2 font-mono text-xs text-foreground outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 pt-1 border-t border-border/60">
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-semibold text-muted-foreground mb-0.5">IMAP Host</label>
                        <input
                          type="text"
                          disabled={!isTeamEditMode}
                          placeholder="imap.company.com"
                          value={editingQueue.dedicatedImapHost || editingQueue.dedicated_imap_host || ''}
                          onChange={(e) => setEditingQueue({ ...editingQueue, dedicatedImapHost: e.target.value, dedicated_imap_host: e.target.value })}
                          className="h-7 w-full rounded border border-input bg-background px-2 font-mono text-xs text-foreground outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-muted-foreground mb-0.5">IMAP Port</label>
                        <input
                          type="text"
                          disabled={!isTeamEditMode}
                          placeholder="993"
                          value={editingQueue.dedicatedImapPort || editingQueue.dedicated_imap_port || '993'}
                          onChange={(e) => setEditingQueue({ ...editingQueue, dedicatedImapPort: e.target.value, dedicated_imap_port: e.target.value })}
                          className="h-7 w-full rounded border border-input bg-background px-2 font-mono text-xs text-foreground outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-muted-foreground mb-0.5">Security</label>
                        <select
                          disabled={!isTeamEditMode}
                          value={editingQueue.dedicatedImapSecurity || editingQueue.dedicated_imap_security || 'SSL'}
                          onChange={(e) => setEditingQueue({ ...editingQueue, dedicatedImapSecurity: e.target.value, dedicated_imap_security: e.target.value })}
                          className="h-7 w-full rounded border border-input bg-background px-1.5 text-xs text-foreground outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          <option value="SSL">SSL</option>
                          <option value="TLS">TLS</option>
                          <option value="STARTTLS">STARTTLS</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Section 4: Team Member Assignments */}
              <div className="p-3.5 rounded-xl border border-border bg-muted/20 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                    <UserCheck className="size-3.5 text-primary" /> Assigned Team Members ({editingTeamAgentIds.length} of {agents.length})
                  </span>
                  <span className="text-xs text-muted-foreground">Select staff assigned to this team</span>
                </div>

                {agents.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-2 italic">No agents registered in staff directory yet.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-36 overflow-y-auto p-1">
                    {agents.map((ag) => {
                      const isSelected = editingTeamAgentIds.includes(String(ag.id));
                      return (
                        <label
                          key={ag.id}
                          className={cn(
                            "flex items-center gap-2.5 p-2 rounded-lg border text-xs transition-colors",
                            isTeamEditMode ? "cursor-pointer" : "cursor-default",
                            isSelected ? "border-primary/50 bg-primary/5" : "border-border bg-card hover:bg-accent/40",
                            !isTeamEditMode && "opacity-80"
                          )}
                        >
                          <input
                            type="checkbox"
                            disabled={!isTeamEditMode}
                            checked={isSelected}
                            onChange={() => {
                              const strId = String(ag.id);
                              setEditingTeamAgentIds((prev) =>
                                isSelected ? prev.filter((id) => id !== strId) : [...prev, strId]
                              );
                            }}
                            className="rounded border-input text-foreground focus:ring-0 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                          />
                          <div className="flex-1 min-w-0">
                            <span className="font-semibold text-foreground truncate block">
                              {ag.first_name} {ag.last_name}
                            </span>
                            <span className="text-xs text-muted-foreground truncate block font-mono">
                              {ag.email || ag.role}
                            </span>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Form Footer */}
              <div className="flex items-center justify-between gap-2 border-t border-border pt-3">
                <button
                  type="button"
                  onClick={() => handleDeleteQueue(editingQueue.queue_id || editingQueue.id || editingQueue.team_id, editingQueue.queue_label || editingQueue.label || editingQueue.team_name)}
                  className="inline-flex h-9 items-center gap-1.5 rounded-md border border-destructive/40 bg-card px-3 text-xs font-semibold text-destructive transition-colors hover:bg-destructive/10"
                >
                  <Trash2 className="size-3.5" /> Delete team
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => { setEditingQueue(null); setIsTeamEditMode(false); }}
                    className="h-9 rounded-md border border-border px-3.5 text-xs font-semibold text-foreground hover:bg-secondary"
                  >
                    Close
                  </button>
                  {isTeamEditMode && (
                    <button
                      type="submit"
                      className="inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-4 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
                    >
                      <Check className="size-3.5" /> Save changes
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT AGENT DRAWER (MULTI-TEAM SELECTION) */}
      {editingAgent && (
        <div className="fixed inset-x-0 bottom-0 top-14 z-50 !m-0 flex justify-end bg-black/50 animate-in fade-in">
          <div className="w-full max-w-md bg-card border-l border-border h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="font-display text-card-title text-foreground">Edit Helpdesk Agent &amp; Team Assignments</h3>
              <button onClick={() => setEditingAgent(null)} className="text-muted-foreground hover:text-foreground cursor-pointer">
                <X className="size-4" />
              </button>
            </div>
            <form onSubmit={handleUpdateAgent} className="flex-1 flex flex-col justify-between overflow-hidden">
              <div className="flex-1 overflow-y-auto p-5 space-y-4 text-xs">
                <div className="grid grid-cols-2 gap-2.5">
                  <div>
                    <label className="font-semibold text-foreground">First Name *</label>
                    <input
                      type="text"
                      required
                      value={editingAgent.first_name}
                      onChange={(e) => setEditingAgent({ ...editingAgent, first_name: e.target.value })}
                      className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                    />
                  </div>
                  <div>
                    <label className="font-semibold text-foreground">Last Name</label>
                    <input
                      type="text"
                      value={editingAgent.last_name}
                      onChange={(e) => setEditingAgent({ ...editingAgent, last_name: e.target.value })}
                      className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                    />
                  </div>
                </div>

                {/* MULTI-TEAM CHECKBOX SELECTOR */}
                <div>
                  <label className="font-semibold text-foreground block mb-1">Assigned Teams (Select 1 or more)</label>
                  <div className="grid grid-cols-2 gap-1.5 p-2 bg-background border border-input rounded-md max-h-32 overflow-y-auto">
                    {queues.map((q) => {
                      const qKey = q.queue_key || q.key || q.team_key || String(q.id || secureRandom());
                      const qLabel = q.queue_label || q.label || q.team_name || q.name || 'Support Team';
                      const isAssigned = (editingAgent.assigned_queues || []).includes(qLabel);
                      return (
                        <label key={qKey} className="flex items-center gap-2 cursor-pointer text-xs p-1 hover:bg-accent rounded">
                          <input
                            type="checkbox"
                            checked={isAssigned}
                            onChange={() => handleToggleEditingAgentQueue(qLabel)}
                            className="rounded border-input text-foreground focus:ring-0 cursor-pointer"
                          />
                          <span className="font-medium text-foreground truncate">{qLabel}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 p-4 border-t border-border bg-card">
                <button
                  type="button"
                  onClick={() => setEditingAgent(null)}
                  className="rounded-md border border-border px-3.5 py-1.5 text-xs font-semibold text-foreground hover:bg-secondary cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-md bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 cursor-pointer shadow-xs"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ADD AGENT DRAWER (MULTI-TEAM ASSIGNMENT) */}
      {showAddAgentModal && (
        <div className="fixed inset-x-0 bottom-0 top-14 z-50 !m-0 flex justify-end bg-black/50 animate-in fade-in">
          <div className="w-full max-w-md bg-card border-l border-border h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="font-display text-card-title text-foreground">Add New Agent &amp; Assign Teams</h3>
              <button onClick={() => setShowAddAgentModal(false)} className="text-muted-foreground hover:text-foreground cursor-pointer">
                <X className="size-4" />
              </button>
            </div>
            <form onSubmit={handleAddAgentSubmit} className="flex-1 flex flex-col justify-between overflow-hidden">
              <div className="flex-1 overflow-y-auto p-5 space-y-4 text-xs">
                <div className="grid grid-cols-2 gap-2.5">
                  <div>
                    <label className="font-semibold text-foreground">First Name *</label>
                    <input
                      type="text"
                      required
                      value={agentForm.first_name}
                      onChange={(e) => setAgentForm({ ...agentForm, first_name: e.target.value })}
                      className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                    />
                  </div>
                  <div>
                    <label className="font-semibold text-foreground">Last Name</label>
                    <input
                      type="text"
                      value={agentForm.last_name}
                      onChange={(e) => setAgentForm({ ...agentForm, last_name: e.target.value })}
                      className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                    />
                  </div>
                </div>

                <div>
                  <label className="font-semibold text-foreground">Email Address *</label>
                  <input
                    type="email"
                    required
                    value={agentForm.email}
                    onChange={(e) => setAgentForm({ ...agentForm, email: e.target.value })}
                    className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2.5 font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>

                {/* MULTI-TEAM CHECKBOX SELECTOR FOR NEW AGENT */}
                <div>
                  <label className="font-semibold text-foreground block mb-1">Assigned Teams (Select 1 or more)</label>
                  <div className="grid grid-cols-2 gap-1.5 p-2 bg-background border border-input rounded-md max-h-32 overflow-y-auto">
                    {queues.map((q) => {
                      const qKey = q.queue_key || q.key || q.team_key || String(q.id || secureRandom());
                      const qLabel = q.queue_label || q.label || q.team_name || q.name || 'Support Team';
                      const isAssigned = agentForm.assigned_queues.includes(qLabel);
                      return (
                        <label key={qKey} className="flex items-center gap-2 cursor-pointer text-xs p-1 hover:bg-accent rounded">
                          <input
                            type="checkbox"
                            checked={isAssigned}
                            onChange={() => handleToggleAgentFormQueue(qLabel)}
                            className="rounded border-input text-foreground focus:ring-0 cursor-pointer"
                          />
                          <span className="font-medium text-foreground truncate">{qLabel}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 p-4 border-t border-border bg-card">
                <button
                  type="button"
                  onClick={() => setShowAddAgentModal(false)}
                  className="rounded-md border border-border px-3.5 py-1.5 text-xs font-semibold text-foreground hover:bg-secondary cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-md bg-primary px-4 py-1.5 text-xs font-semibold text-white hover:bg-primary/90 shadow-xs cursor-pointer"
                >
                  Register Agent
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT WORKFLOW DRAWER */}
      {editingWorkflow && (
        <div className="fixed inset-x-0 bottom-0 top-14 z-50 !m-0 flex justify-end bg-black/50 animate-in fade-in">
          <div className="w-full max-w-md bg-card border-l border-border h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="font-display text-card-title text-foreground">Edit Ticket Type &amp; Workflow</h3>
              <button onClick={() => setEditingWorkflow(null)} className="text-muted-foreground hover:text-foreground cursor-pointer">
                <X className="size-4" />
              </button>
            </div>
            <form onSubmit={handleUpdateWorkflow} className="flex-1 flex flex-col justify-between overflow-hidden">
              <div className="flex-1 overflow-y-auto p-5 space-y-4 text-xs">
                <div>
                  <label className="font-semibold text-foreground">Ticket Type Name *</label>
                  <input
                    type="text"
                    required
                    value={editingWorkflow.type}
                    onChange={(e) => setEditingWorkflow({ ...editingWorkflow, type: e.target.value })}
                    className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="font-semibold text-foreground">Description</label>
                  <textarea
                    rows={2}
                    value={editingWorkflow.description}
                    onChange={(e) => setEditingWorkflow({ ...editingWorkflow, description: e.target.value })}
                    className="mt-1 w-full rounded-md border border-input bg-background p-2.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="font-semibold text-foreground">State Transitions (Comma Separated)</label>
                  <input
                    type="text"
                    value={editingWorkflow.states ? editingWorkflow.states.join(', ') : ''}
                    onChange={(e) =>
                      setEditingWorkflow({
                        ...editingWorkflow,
                        states: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                      })
                    }
                    className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2.5 font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 p-4 border-t border-border bg-card">
                <button
                  type="button"
                  onClick={() => setEditingWorkflow(null)}
                  className="rounded-md border border-border px-3.5 py-1.5 text-xs font-semibold text-foreground hover:bg-secondary cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-md bg-primary px-4 py-1.5 text-xs font-semibold text-white hover:bg-primary/90 shadow-xs cursor-pointer"
                >
                  Save Workflow
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ADD WORKFLOW DRAWER */}
      {showAddWorkflowModal && (
        <div className="fixed inset-x-0 bottom-0 top-14 z-50 !m-0 flex justify-end bg-black/50 animate-in fade-in">
          <div className="w-full max-w-md bg-card border-l border-border h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="font-display text-card-title text-foreground">Add New Ticket Type &amp; Workflow</h3>
              <button onClick={() => setShowAddWorkflowModal(false)} className="text-muted-foreground hover:text-foreground cursor-pointer">
                <X className="size-4" />
              </button>
            </div>
            <form onSubmit={handleAddWorkflowSubmit} className="flex-1 flex flex-col justify-between overflow-hidden">
              <div className="flex-1 overflow-y-auto p-5 space-y-4 text-xs">
                <div>
                  <label className="font-semibold text-foreground">Ticket Type Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Infrastructure Request"
                    value={workflowForm.type}
                    onChange={(e) => setWorkflowForm({ ...workflowForm, type: e.target.value })}
                    className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>

                <div>
                  <label className="font-semibold text-foreground">Description</label>
                  <input
                    type="text"
                    placeholder="Brief workflow description"
                    value={workflowForm.description}
                    onChange={(e) => setWorkflowForm({ ...workflowForm, description: e.target.value })}
                    className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>

                <div>
                  <label className="font-semibold text-foreground">State Transitions (Comma Separated)</label>
                  <input
                    type="text"
                    value={workflowForm.statesStr}
                    onChange={(e) => setWorkflowForm({ ...workflowForm, statesStr: e.target.value })}
                    className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2.5 font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 p-4 border-t border-border bg-card">
                <button
                  type="button"
                  onClick={() => setShowAddWorkflowModal(false)}
                  className="rounded-md border border-border px-3.5 py-1.5 text-xs font-semibold text-foreground hover:bg-secondary cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-md bg-primary px-4 py-1.5 text-xs font-semibold text-white hover:bg-primary/90 shadow-xs cursor-pointer"
                >
                  Create Type
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ADD NEW EMAIL TEMPLATE DRAWER */}
      {showAddTemplateModal && (
        <div className="fixed inset-x-0 bottom-0 top-14 z-50 !m-0 flex justify-end bg-black/50 animate-in fade-in">
          <div className="w-full max-w-md bg-card border-l border-border h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="font-display text-card-title text-foreground">Add New Email Template</h3>
              <button onClick={() => setShowAddTemplateModal(false)} className="text-muted-foreground hover:text-foreground cursor-pointer">
                <X className="size-4" />
              </button>
            </div>
            <form onSubmit={handleAddTemplateSubmit} className="flex-1 flex flex-col justify-between overflow-hidden">
              <div className="flex-1 overflow-y-auto p-5 space-y-4 text-xs">
                <div>
                  <label className="font-semibold text-foreground">Template Display Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Ticket Resolved Feedback Request"
                    value={newTemplateForm.name}
                    onChange={(e) => setNewTemplateForm({ ...newTemplateForm, name: e.target.value })}
                    className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>

                <div>
                  <label className="font-semibold text-foreground">Category</label>
                  <select
                    value={newTemplateForm.category}
                    onChange={(e) => setNewTemplateForm({ ...newTemplateForm, category: e.target.value })}
                    className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  >
                    <option value="notification">Notification</option>
                    <option value="alert">Alert / Escalation</option>
                    <option value="followup">Follow-up</option>
                  </select>
                </div>

                <div>
                  <label className="font-semibold text-foreground">Subject Line Template *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Update on Ticket #{ticket_id}: {subject}"
                    value={newTemplateForm.subject}
                    onChange={(e) => setNewTemplateForm({ ...newTemplateForm, subject: e.target.value })}
                    className="mt-1 h-8 w-full rounded-md border border-input bg-background px-2.5 font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>

                <div>
                  <label className="font-semibold text-foreground">Email Body Template</label>
                  <textarea
                    rows={4}
                    placeholder="Dear {client_name}, ..."
                    value={newTemplateForm.body}
                    onChange={(e) => setNewTemplateForm({ ...newTemplateForm, body: e.target.value })}
                    className="mt-1 w-full rounded-md border border-input bg-background p-2.5 font-mono text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 p-4 border-t border-border bg-card">
                <button
                  type="button"
                  onClick={() => setShowAddTemplateModal(false)}
                  className="rounded-md border border-border px-3.5 py-1.5 text-xs font-semibold text-foreground hover:bg-secondary cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-md bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 shadow-xs cursor-pointer"
                >
                  Create Template
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

